// Import both result services
import {ResultService as USResultService} from './pc-results-service-us.js';
import {ResultService as NonUSResultService} from './pc-results-service-all.js';

// Determine which ResultService to use based on ISO code
const isUSMarket = (window.isoCode === 'us');
const ResultService = isUSMarket ? USResultService : NonUSResultService;

// Determine PC API endpoint
const pcApiEndpointConfig = {
  baf: {
    development: 'https://protein.testbodyandfit.com',
    production: 'https://protein.bodyandfit.com'
  },
  on: {
    development: 'https://protein-test.optimumnutrition.com',
    production: 'https://protein.optimumnutrition.com'
  },
  default: {
    development: 'https://protein-test.optimumnutrition.com',
    production: 'https://protein.optimumnutrition.com'
  }
};
const pcApiEndpoint = (window.shopBrand) ? pcApiEndpointConfig[window.shopBrand][window.shopEnv] : pcApiEndpointConfig['default'][window.shopEnv];

// Define constants
const KG_TO_LBS_FACTOR = 2.20462;
const HEIGHT_ROUND_BY = 2.54;

// Define global variables outside any function to avoid redeclaration issues
let loadingOverlay, resultsContent, quizContent, quizContentWrapper, errorPanel, contentPanel, quizNavigation, progressBar;

// Removed convertWeight helper; weight conversion handled in weight-specific listeners

document.addEventListener('DOMContentLoaded', function() {
  // Apply quiz options columns from theme settings
  function applyQuizOptionsColumns() {
    // Get default columns setting from theme settings (parse as integer, default to 2)
    const defaultColumns = parseInt(window.proteinCalcSettings?.quizOptionsColumns || '2', 10) || 2;

    // Apply to all quiz-options containers based on their step type
    document.querySelectorAll('.quiz-options').forEach(optionsContainer => {
      // Respect Liquid-driven layout classes: if present, don't override with inline styles
      const hasLiquidLayout = optionsContainer.classList.contains('quiz-options--3') || optionsContainer.classList.contains('quiz-options--4');
      if (hasLiquidLayout) {
        // Ensure any previous inline override is removed so CSS classes take effect
        optionsContainer.style.removeProperty('grid-template-columns');
        return;
      }
      // Find the parent step element
      const stepElement = optionsContainer.closest('.quiz-step');
      if (!stepElement) {
        // If no parent step found, use default columns
        optionsContainer.style.gridTemplateColumns = `repeat(${defaultColumns}, 1fr)`;
        return;
      }

      // Get step type from data attributes or class names
      let stepType = '';
      if (stepElement.classList.contains('gender-step') || stepElement.dataset.stepType === 'gender') {
        stepType = 'gender';
      } else if (stepElement.classList.contains('age-step') || stepElement.dataset.stepType === 'age') {
        stepType = 'age';
      } else if (stepElement.classList.contains('goal-step') || stepElement.dataset.stepType === 'goal') {
        stepType = 'goal';
      } else if (stepElement.classList.contains('activity-step') || stepElement.dataset.stepType === 'activity') {
        stepType = 'activity';
      } else if (stepElement.classList.contains('exercise-step') || stepElement.dataset.stepType === 'exercise') {
        stepType = 'exercise';
      } else if (stepElement.classList.contains('health-step') || stepElement.dataset.stepType === 'health') {
        stepType = 'health';
      } else if (stepElement.classList.contains('training-step') || stepElement.dataset.stepType === 'training') {
        stepType = 'training';
      } else if (stepElement.classList.contains('diet-step') || stepElement.dataset.stepType === 'diet') {
        stepType = 'diet';
      }

      // Get the columns setting for this step type (or use default)
      let columns = defaultColumns;
      if (stepType) {
        const settingKey = `${stepType}StepColumns`;
        // Parse the text field value as integer, fallback to default if invalid
        const settingValue = window.proteinCalcSettings?.[settingKey];
        if (settingValue) {
          const parsedValue = parseInt(settingValue, 10);
          if (!isNaN(parsedValue) && parsedValue > 0) {
            columns = parsedValue;
          }
        }
      }

      // Apply the columns setting (ensure it's at least 1)
      const safeColumns = Math.max(1, columns);
      optionsContainer.style.gridTemplateColumns = `repeat(${safeColumns}, 1fr)`;
    });
  }

  // Call the function on page load
  applyQuizOptionsColumns();

  // Initialize the theme settings object if not already done
  window.proteinCalcSettings = window.proteinCalcSettings || {};
    
  window.proteinCalcI18n = window.proteinCalcI18n || {};
  window.proteinCalcI18n.submitting = window.proteinCalcI18n.submitting || "Submitting...";
  window.proteinCalcI18n.genericError = window.proteinCalcI18n.genericError || "Something went wrong. Please try again.";
  window.proteinCalcI18n.requiredField = window.proteinCalcI18n.requiredField || "This field is required";
  window.proteinCalcI18n.noMacroInfo = window.proteinCalcI18n.noMacroInfo || "No macro nutrient information available.";
  window.proteinCalcI18n.proteinLabel = window.proteinCalcI18n.proteinLabel || "protein";
  window.proteinCalcI18n.carbsLabel = window.proteinCalcI18n.carbsLabel || "carbs";
  window.proteinCalcI18n.fatLabel = window.proteinCalcI18n.fatLabel || "fat";
  window.proteinCalcI18n.caloriesLabel = window.proteinCalcI18n.caloriesLabel || "calories";
  window.proteinCalcI18n.unitGrams = window.proteinCalcI18n.unitGrams || "g";
  window.proteinCalcI18n.unitKcal = window.proteinCalcI18n.unitKcal || " kcal";
  window.proteinCalcI18n.macroItemTitleFallback = window.proteinCalcI18n.macroItemTitleFallback || "Macro Nutrient";
  window.proteinCalcI18n.notAvailable = window.proteinCalcI18n.notAvailable || "N\/A";
  window.proteinCalcI18n.emailPlaceholder = window.proteinCalcI18n.emailPlaceholder || "Enter your email";  
  window.proteinCalcI18n.a11yPrevious = window.proteinCalcI18n.a11yPrevious || "Previous";
  window.proteinCalcI18n.a11yNext = window.proteinCalcI18n.a11yNext || "Next";

  // Set marketing checkbox state based on locale
  const marketingCheckbox = document.getElementById('marketing_checkbox');
  if (marketingCheckbox) {
    const currentLocale = (window.localeCode || '').toLowerCase();

    // Get the locale lists from theme settings
    const autoCheckedLocales = window.proteinCalcSettings.marketingCheckboxAutoCheckedLocales?.split(',').map(locale => locale.trim()) || [];
    const notCheckedLocales = window.proteinCalcSettings.marketingCheckboxNotCheckedLocales?.split(',').map(locale => locale.trim()) || [];

    if (currentLocale) {
      if (notCheckedLocales.includes(currentLocale)) {
        // Explicitly unchecked for these locales
        marketingCheckbox.checked = false;
      } else if (autoCheckedLocales.includes(currentLocale)) {
        // Explicitly checked for these locales
        marketingCheckbox.checked = true;
      } else {
        // Default to checked if not in either list
        marketingCheckbox.checked = true;
      }
    }
  }

  // Helpers for weight selects (integer + decimal)
  function clampWeight(value, unit) {
    const bounds = unit === 'kg' ? { min: 3.0, max: 362.9 } : { min: 6.0, max: 798.9 };
    if (isNaN(value)) return bounds.min;
    return Math.max(bounds.min, Math.min(value, bounds.max));
  }

  function splitToIntDecRounded(value) {
    const rounded = Math.round(value * 10) / 10;
    let intPart = Math.floor(rounded);
    let decDigit = Math.round((rounded - intPart) * 10);
    if (decDigit === 10) {
      intPart += 1;
      decDigit = 0;
    }
    return { intPart, decDigit };
  }

  // Add direct event listeners for weight unit toggle buttons
  const kgButton = document.querySelector('button[data-unit="kg"]');
  const lbButton = document.querySelector('button[data-unit="lbs"]');

  if (kgButton && lbButton) {
    // Add new event listeners
    kgButton.addEventListener('click', function() {
      if (!this.classList.contains('active')) {
        // Update button states
        lbButton.classList.remove('active');
        this.classList.add('active');
        const weightInput = document.getElementById('weight');
        const kgContainer = document.getElementById('weight-kg-container');
        const lbContainer = document.getElementById('weight-lb-container');
        const kgIntSel = document.getElementById('weight-kg-int');
        const kgDecSel = document.getElementById('weight-kg-dec');
        const lbIntSel = document.getElementById('weight-lb-int');

        if (weightInput && kgIntSel && kgDecSel && lbIntSel) {
          // Convert from current lbs selects to kg and sync kg selects
          const lbsVal = parseInt(lbIntSel.value, 10) || 0;
          const kgValClamped = clampWeight(lbsVal / KG_TO_LBS_FACTOR, 'kg');
          const { intPart, decDigit } = splitToIntDecRounded(kgValClamped);
          kgIntSel.value = String(intPart);
          kgDecSel.value = String(decDigit);

          // Show kg selectors, hide lbs selectors
          if (kgContainer) kgContainer.style.display = 'flex';
          if (lbContainer) lbContainer.style.display = 'none';

          // Update hidden input and dataset for roundtrip
          weightInput.value = (intPart + decDigit / 10);
          weightInput.dataset.kgValue = String(kgValClamped);
          weightInput.dispatchEvent(new Event('input', { bubbles: true }));
        } else if (weightInput) {
          // Fallback to legacy behavior using input only
          const currentValue = parseFloat(weightInput.value) || 0;
          const newValue = Math.round(currentValue / KG_TO_LBS_FACTOR);
          weightInput.value = newValue;
          if (typeof quizData !== 'undefined') quizData.weightInKg = newValue;
          weightInput.dispatchEvent(new Event('input'));
        }
      }
    });

    lbButton.addEventListener('click', function() {
      if (!this.classList.contains('active')) {
        // Update button states
        kgButton.classList.remove('active');
        this.classList.add('active');
        const weightInput = document.getElementById('weight');
        const kgContainer = document.getElementById('weight-kg-container');
        const lbContainer = document.getElementById('weight-lb-container');
        const kgIntSel = document.getElementById('weight-kg-int');
        const kgDecSel = document.getElementById('weight-kg-dec');
        const lbIntSel = document.getElementById('weight-lb-int');

        if (weightInput && kgIntSel && kgDecSel && lbIntSel) {
          // Convert from current kg selects to lbs and sync lbs selects
          const kgVal = (parseInt(kgIntSel.value, 10) || 0) + ((parseInt(kgDecSel.value, 10) || 0) / 10);
          const lbsValClamped = clampWeight(kgVal * KG_TO_LBS_FACTOR, 'lbs');
          const { intPart, decDigit } = splitToIntDecRounded(lbsValClamped);
          lbIntSel.value = String(intPart);

          // Show lbs selectors, hide kg selectors
          if (kgContainer) kgContainer.style.display = 'none';
          if (lbContainer) lbContainer.style.display = 'flex';

          // Update hidden input and dataset for roundtrip
          weightInput.value = (intPart + decDigit / 10);
          weightInput.dataset.kgValue = String(kgVal); // keep original kg for roundtrip
          weightInput.dispatchEvent(new Event('input', { bubbles: true }));
        } else if (weightInput) {
          // Fallback to legacy behavior using input only
          const currentValue = parseFloat(weightInput.value) || 0;
          const newValue = Math.round(currentValue * KG_TO_LBS_FACTOR);
          weightInput.value = newValue;
          if (typeof quizData !== 'undefined') quizData.weightInKg = currentValue;
          weightInput.dispatchEvent(new Event('input'));
        }
      }
    });
  }

  // Initialize all DOM references
  loadingOverlay = document.querySelector('.loading-overlay');
  resultsContent = document.querySelector('.results-content');
  quizContent = document.querySelector('.quiz-content');


  function enforceMaxValue(input) {
    if (!input) return;

    // Special handling for weight input to allow decimals
    const isWeightInput = input.id === 'weight';

    if (isWeightInput) {
      // For weight input, we'll use a simpler approach that works reliably
      // The input type is already set to 'text' in the HTML

      // Handle input events with a simple approach
      input.addEventListener('input', function() {
        // Get current value and cursor position
        const cursorPos = this.selectionStart;
        let value = this.value;

        // Only allow digits and one decimal point
        value = value.replace(/[^0-9.]/g, '');

        // Handle multiple decimal points - keep only the first
        const decimalPoints = value.match(/\./g);
        if (decimalPoints && decimalPoints.length > 1) {
          const firstDecimalPos = value.indexOf('.');
          value = value.substring(0, firstDecimalPos + 1) +
                 value.substring(firstDecimalPos + 1).replace(/\./g, '');
        }

        // Apply limits: max 3 digits before decimal, 2 after
        const parts = value.split('.');
        if (parts[0].length > 3) {
          parts[0] = parts[0].substring(0, 3);
        }

        if (parts.length > 1 && parts[1].length > 2) {
          parts[1] = parts[1].substring(0, 2);
        }

        // Reassemble value
        const newValue = parts[0] + (parts.length > 1 ? '.' + parts[1] : '');

        // Only update if value changed to avoid cursor jumping
        if (newValue !== this.value) {
          // Calculate cursor position adjustment
          const lengthDiff = newValue.length - this.value.length;

          // Update value
          this.value = newValue;

          // Restore cursor position
          this.setSelectionRange(cursorPos + lengthDiff, cursorPos + lengthDiff);
        }
      });

      // Also check on blur for max value
      input.addEventListener('blur', function() {
        if (this.value && !isNaN(parseFloat(this.value))) {
          const numValue = parseFloat(this.value);
          if (numValue > 999.99) {
            this.value = '999.99';
          }
        }
      });
    } else if (input.id === 'height') {
      // For height input, use the same logic as weight to allow decimals
      input.addEventListener('input', function() {
        const cursorPos = this.selectionStart;
        let value = this.value;
        value = value.replace(/[^0-9.]/g, '');
        const decimalPoints = value.match(/\./g);
        if (decimalPoints && decimalPoints.length > 1) {
          const firstDecimalPos = value.indexOf('.');
          value = value.substring(0, firstDecimalPos + 1) + value.substring(firstDecimalPos + 1).replace(/\./g, '');
        }
        const parts = value.split('.');
        if (parts[0].length > 3) {
          parts[0] = parts[0].substring(0, 3);
        }
        if (parts.length > 1 && parts[1].length > 2) {
          parts[1] = parts[1].substring(0, 2);
        }
        const newValue = parts[0] + (parts.length > 1 ? '.' + parts[1] : '');
        if (newValue !== this.value) {
          const lengthDiff = newValue.length - this.value.length;
          this.value = newValue;
          this.setSelectionRange(cursorPos + lengthDiff, cursorPos + lengthDiff);
        }
      });
      input.addEventListener('blur', function() {
        if (this.value && !isNaN(parseFloat(this.value))) {
          const numValue = parseFloat(this.value);
          if (numValue > 999.99) {
            this.value = '999.99';
          }
        }
      });
    }

    // Also check on blur to catch any values that might have been entered programmatically
    input.addEventListener('blur', function() {
      if (isWeightInput) {
        // For weight, validate the decimal format
        let value = this.value;
        if (value) {
          const floatValue = parseFloat(value);
          if (floatValue > 999.99) {
            this.value = '999.99';
          } else {
            // Format to ensure proper decimal display
            this.value = floatValue.toString();
          }
        }
      } else {
        // For other inputs, keep original behavior
        if (this.value.length > 3 || parseInt(this.value) > 999) {
          this.value = Math.min(parseInt(this.value.slice(0, 3)), 999);
        }
      }
    });
  }

  // Initialize the validation when inputs become available
  function initInputValidation() {
    const heightInput = document.getElementById('height');
    const weightInput = document.getElementById('weight');

    if (heightInput) enforceMaxValue(heightInput);
    if (weightInput) {
      // For weight input, we need to ensure it accepts decimal values
      weightInput.setAttribute('type', 'text');
      weightInput.setAttribute('inputmode', 'decimal');
      enforceMaxValue(weightInput);
    }
  }

  // Call initially and also when showing steps (since inputs might not be in DOM yet)
  initInputValidation();

  // Weight toggle listeners are set up earlier using direct button handlers

  // Set up a unified mutation observer to handle all DOM changes
  const unifiedObserver = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      // Handle added nodes (for weight input and general input validation)
      if (mutation.addedNodes && mutation.addedNodes.length > 0) {
        // Check for specific weight input
        for (let i = 0; i < mutation.addedNodes.length; i++) {
          const node = mutation.addedNodes[i];
          if (node.id === 'weight') {
            node.setAttribute('type', 'text');
            node.setAttribute('inputmode', 'decimal');
            enforceMaxValue(node);
          }
        }

        // Run general input validation for any new inputs
        initInputValidation();
      }

      // Handle style changes for results content
      if (mutation.target === resultsContent && mutation.attributeName === 'style') {
        hideNavigationOnResults();
      }
    });
  });

  // Observe document body for DOM changes with a single observer
  unifiedObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style']
  });

  // Check if rec_id is in the URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const recId = urlParams.get('rec_id');
  const customerId = urlParams.get('customer_id');

  if (recId && customerId) {
    // First, make sure the modal is displayed
    const modal = document.getElementById('proteinCalculatorModal');
    if (modal) {
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
    }

    // Hide signup form when rec_id is present (scroll button is not available here, hide it later)
    const signupForm = document.querySelector('.signup-form');
    if (signupForm) signupForm.style.display = 'none';

    // Show loading overlay immediately
    if (loadingOverlay) {
      loadingOverlay.style.display = 'flex';
    }

    // Hide quiz content
    if (quizContent) {
      quizContent.style.display = 'none';
    }

    // Hide quiz content wrapper
    const quizContentWrapper = document.querySelector('.quiz-content-wrapper');
    if (quizContentWrapper) {
      quizContentWrapper.style.display = 'none';
    }

    // Hide navigation and progress bar
    const quizNavigation = document.querySelector('.quiz-navigation');
    const progressBar = document.querySelector('.protein-calculator-modal__progress');
    if (quizNavigation) quizNavigation.style.display = 'none';
    if (progressBar) progressBar.style.display = 'none';

    // Fetch results from API after a short delay to ensure DOM is ready
    setTimeout(() => {
      fetchResultsByRecId(recId, customerId);

      // Add a delayed check to ensure body overflow is reset + hide scroll button
      setTimeout(() => {
        if (document.body.style.overflow === 'hidden') {
          document.body.style.overflow = 'auto';
        }
      }, 3000); // Check after 3 seconds
    }, 100);
  }
  quizContentWrapper = document.querySelector('.quiz-content-wrapper');
  errorPanel = document.querySelector('.macro-calculator-results-panel__error');
  contentPanel = document.querySelector('.macro-calculator-results-panel__content');
  quizNavigation = document.getElementById('quizNavigation');
  progressBar = document.querySelector('.protein-calculator-modal__progress');
  // Function to hide navigation and progress bar when results are shown
  function hideNavigationOnResults() {
    // Use global variables instead of redeclaring them

    if (resultsContent) {
      // Check if results are visible
      if (resultsContent.style.display !== 'none') {
        // Hide quiz elements
        if (quizNavigation) quizNavigation.style.display = 'none';
        if (progressBar) progressBar.style.display = 'none';
        if (quizContentWrapper) quizContentWrapper.style.display = 'none';

        // Show the recommended products disclaimer
        const recommendedProductsDisclaimer = document.querySelector('.recommended-products-disclaimer');
        if (recommendedProductsDisclaimer) {
          recommendedProductsDisclaimer.style.display = 'block';
        }

        // Center the results content
        resultsContent.style.display = 'flex';
        resultsContent.style.alignItems = 'center';
        resultsContent.style.justifyContent = 'center';
        resultsContent.style.height = '100%';
      } else {
        // Show quiz elements
        if (quizNavigation) quizNavigation.style.display = 'flex';
        if (progressBar) progressBar.style.display = 'block';
        if (quizContentWrapper) quizContentWrapper.style.display = 'block';
      }
    }
  }

  // Note: Style changes on results-content are now handled by the unified observer above
  // Initialize modal controls
  const modal = document.getElementById('proteinCalculatorModal');
  const openButton = document.getElementById('openProteinCalculator');
  const closeButton = document.getElementById('closeProteinCalculator');
  // Rename to avoid conflict with global progressBar variable
  const progressBarFill = document.querySelector('.protein-calculator-modal__progress-bar-fill');

  // Helper: switch weight UI to LBS when available
  function setWeightDefaultToLbs() {
    try {
      const lbBtn = document.getElementById('weight-lb-btn');
      if (isUSMarket && lbBtn && !lbBtn.classList.contains('active')) {
        lbBtn.click(); // triggers existing listeners and conversion + UI toggle
      }
    } catch (e) { /* noop */ }
  }

  // Helper: switch height UI to feet/inches when available
  function setHeightDefaultToInches() {
    if (!isUSMarket) return;
    const heightField = document.getElementById('height-field');
    if (!heightField) return;

    const toggle = document.getElementById('height-toggle');
    const cmBtn = toggle?.querySelector('[data-unit="cm"]');
    const inBtn = toggle?.querySelector('[data-unit="in"]');
    const cmSelectContainer = heightField.querySelector('.cm-select-container');
    const cmSelect = heightField.querySelector('#height-cm-select');
    const input = document.getElementById('height');

    // Ensure feet/inches container exists by invoking the builder logic indirectly
    // The builder attaches to input events and creates .feet-inches-container when input.id === 'height'
    // Trigger a synthetic input event once to ensure setup code has run
    if (input) input.dispatchEvent(new Event('input', { bubbles: true }));

    let feetInchesContainer = heightField.querySelector('.feet-inches-container');

    // If not created yet, create in place (fallback)
    if (!feetInchesContainer) {
      feetInchesContainer = document.createElement('div');
      feetInchesContainer.className = 'feet-inches-container';
      feetInchesContainer.style.display = 'none';

      const feetSelect = document.createElement('select');
      feetSelect.id = 'height-feet';
      feetSelect.className = 'feet-select';
      for (let i = 1; i <= 9; i++) {
        const opt = document.createElement('option');
        opt.value = i; opt.textContent = i + ' ft';
        feetSelect.appendChild(opt);
      }

      const inchesSelect = document.createElement('select');
      inchesSelect.id = 'height-inches';
      inchesSelect.className = 'inches-select';
      for (let i = 0; i <= 11; i++) {
        const opt = document.createElement('option');
        opt.value = i; opt.textContent = i + ' in';
        inchesSelect.appendChild(opt);
      }

      // Append raw selects; rely on CSS for caret and layout
      feetInchesContainer.appendChild(feetSelect);
      feetInchesContainer.appendChild(inchesSelect);
      input?.parentNode?.insertBefore(feetInchesContainer, input.nextSibling);
    }

    // Now switch UI to inches using the same logic as initial setup
    let cmBaseline = parseFloat(input?.dataset.cmValue || input?.value) || 0;
    if (!cmBaseline && cmSelect) cmBaseline = parseInt(cmSelect.value, 10) || 0;
    if (cmBaseline) {
      if (inBtn) {
        cmBtn?.classList.remove('active');
        inBtn.classList.add('active');
      }
      const totalInches = parseFloat((cmBaseline / HEIGHT_ROUND_BY).toFixed(4));
      let feet = Math.floor(totalInches / 12);
      let inches = Math.round(totalInches - feet * 12);
      if (feet < 1) { feet = 1; inches = 0; }

      const feetSelectEl = heightField.querySelector('#height-feet');
      const inchesSelectEl = heightField.querySelector('#height-inches');
      if (feetSelectEl) feetSelectEl.value = String(feet);
      if (inchesSelectEl) inchesSelectEl.value = String(inches);

      if (cmSelectContainer) cmSelectContainer.style.display = 'none';
      if (feetInchesContainer) feetInchesContainer.style.display = 'flex';
      if (input) {
        input.style.display = 'none';
        // Preserve the original cm baseline so subsequent init logic uses centimeters, not inches
        input.dataset.cmValue = cmBaseline;
        // Store total inches in the hidden input for unit='in' workflow
        input.value = totalInches;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }

  function applyUSDefaults() {
    if (!isUSMarket) return;
    setWeightDefaultToLbs();
    setHeightDefaultToInches();
  }

  // Handle modal open/close - with proper null checks
  if (openButton) {
    openButton.addEventListener('click', () => {
      if (modal) {
        modal.style.display = 'flex';
        document.body.style.overflow = 'hidden';
        initQuiz();
        // Apply US defaults after quiz is initialized
        applyUSDefaults();
        // Track start of quiz
        if (window.dataLayerHelper !== undefined) {
          window.dataLayerHelper.track('P_C_StartQuiz');
        }
        trapFocus(modal);
      }
    });
  }

  if (closeButton) {
    closeButton.addEventListener('click', () => {
      if (modal) {
        modal.style.display = 'none';
        document.body.style.overflow = '';
        openButton.focus();
      }
    });
  }

  // Close modal on outside click
  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.style.display = 'none';
        document.body.style.overflow = '';
        openButton.focus();
      }
    });
  }

  // Apply US defaults once on initial load as well (in case modal content is already in DOM)
  applyUSDefaults();

  // Focus trap inside the contianer
  function trapFocus(container) {
    const focusableSelectors = [
      'a[href]', 'area[href]', 'input:not([disabled])',
      'select:not([disabled])', 'textarea:not([disabled])',
      'button:not([disabled])', 'iframe', 'object', 'embed',
      '[tabindex]:not([tabindex="-1"])', '[contenteditable]'
    ];

    container.addEventListener('keydown', function(e) {
      if (e.key !== 'Tab') return;

      const focusableElements = Array.from(container.querySelectorAll(focusableSelectors.join(',')))
        .filter(el => el.offsetParent !== null); // tylko widoczne

      if (focusableElements.length === 0) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  // Handle scroll to signup form
  document.getElementById('scrollToSignupForm')?.addEventListener('click', function(e) {
    e.preventDefault();
    const disclaimer = document.querySelector('.signup-disclaimer');
    if (disclaimer) {
      // Scroll to position the disclaimer element at the top of the viewport
      disclaimer.scrollIntoView({ behavior: 'auto', block: 'start' });

      // Calculate offset to scroll just past the disclaimer
      // Get the height of the disclaimer plus some additional padding
      const disclaimerHeight = disclaimer.offsetHeight;
      const additionalOffset = 120; // Extra padding below the disclaimer (increased by 100px)

      // Scroll past the disclaimer so the form is in better view
      setTimeout(() => {
        window.scrollBy({
          top: disclaimerHeight + additionalOffset,
          behavior: 'smooth'
        });
      }, 100);
    } else {
      // Fallback to the original form scrolling if disclaimer not found
      const form = document.querySelector('.signup-form');
      if (form) {
        form.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }
  });



  let currentStep = 1;
  let totalSteps = 11;
  let isNavigating = false; // Flag to prevent double navigation

  // Add error message and button styling
  const style = document.createElement('style');
  style.textContent = `
    .error-message {
      color: #ff4d4d;
      font-size: 14px;
      margin-top: 8px;
      display: none;
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    .quiz-step.has-error .error-message {
      display: block;
    }
    .quiz-step.has-error input {
      /* Removed red border */
    }
    .quiz-step.has-error .quiz-options {
      /* Removed red border */
    }
    .quiz-step {
      /* Removed transition for instant step changes */
    }
    .quiz-step.hiding {
      display: none;
    }
    .next-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `;
  document.head.appendChild(style);

  // Function to show error message
  function showError(step, message) {
    if (!message) { message = window.proteinCalcI18n.requiredField; }
    step.classList.add('has-error');
    const errorDiv = step.querySelector('.error-message');
    if (errorDiv) {
      errorDiv.textContent = message;
      errorDiv.style.display = 'block';
      setTimeout(() => errorDiv.style.opacity = '1', 10);
    }
  }

  // Function to clear error message
  function clearError(step) {
    step.classList.remove('has-error');
    const errorDiv = step.querySelector('.error-message');
    if (errorDiv) {
      errorDiv.style.opacity = '0';
      errorDiv.style.display = 'none';
    }
  }

  // Add error messages to each step
  document.querySelectorAll('.quiz-step').forEach(step => {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'error-message';
    errorDiv.textContent = window.proteinCalcI18n.requiredField;
    step.appendChild(errorDiv);
  });

  let quizData = {
    gender: '',
    ageInYears: '',
    heightInCm: '',
    weightInKg: '',
    goal: '',
    occupationalPal: '',
    exercisePal: '',
    needs: '',
    favWorkouts: '',
    diet: '',
    email: '',
    name: ''
  };

  let nextButton = document.getElementById('nextStep');
  let prevButton = document.getElementById('prevStep');

  // Prevent form submissions inside the protein calculator modal only
  document.querySelectorAll('#proteinCalculatorModal form').forEach(form => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      return false;
    });
  });

  // Handle quiz option clicks
  document.querySelectorAll('.quiz-option-input').forEach(option => {
    option.addEventListener('click', function(e) {
      e.stopPropagation();

      const step = this.closest('.quiz-step');
      const value = this.getAttribute('value');

      // Clear error state when an option is selected
      clearError(step);

      if (currentStep === 9) { // Health goals - max 2
        if(this.checked) {
          const selectedGoals = step.querySelectorAll('.quiz-option-input:checked').length;

          if (selectedGoals >= 2) {
            step.querySelectorAll('.quiz-option-input').forEach(input => {
              if (!input.checked) {
                input.disabled = true;
              }
            });
          }
        } else {
          step.querySelectorAll('.quiz-option-input').forEach(input => {
            if (!input.checked) {
              input.disabled = false;
            }
          });
        }
        // Update needs as comma-separated string
        const selectedNeeds = Array.from(step.querySelectorAll('.quiz-option-input:checked'))
          .map(opt => opt.getAttribute('value'));
        quizData.needs = selectedNeeds.join(',');
        updateNextButtonState();
      } else if (currentStep === 10) { // Training types - max 3
        if (isUSMarket) {
          // US: enforce single-select behavior even though inputs are checkboxes
          step.querySelectorAll('.quiz-option-input').forEach(input => {
            if (input !== this) {
              input.checked = false;
            }
            // Ensure all remain enabled for switching selection
            input.disabled = false;
          });

          // Store a single value for US
          quizData.favWorkouts = this.checked ? value : '';
        } else {
          // Non-US: allow up to 3 selections
          if(this.checked) {
            const selectedGoals = step.querySelectorAll('.quiz-option-input:checked').length;

            if (selectedGoals >= 3) {
              step.querySelectorAll('.quiz-option-input').forEach(input => {
                if (!input.checked) {
                  input.disabled = true;
                }
              });
            }
          } else {
            step.querySelectorAll('.quiz-option-input').forEach(input => {
              if (!input.checked) {
                input.disabled = false;
              }
            });
          }
          // Update favWorkouts as comma-separated string
          const selectedWorkouts = Array.from(step.querySelectorAll('.quiz-option-input:checked'))
            .map(opt => opt.getAttribute('value'));
          quizData.favWorkouts = selectedWorkouts.join(',');
        }
        updateNextButtonState();

      } else {
        // Single selection for other steps
        step.querySelectorAll('.quiz-option-input').forEach(opt => opt.classList.remove('selected'));
        this.classList.add('selected');

        switch(currentStep) {
          case 2:
            quizData.gender = value;
            updateNextButtonState();
            break;
          case 3:
            quizData.ageInYears = parseInt(value);
            // Check if this is the 'Under 18' option (value='17') or has the disabler_option setting
            const isUnder18 = value === '17';
            const isDisablerOption = this.hasAttribute('data-disabler') && this.getAttribute('data-disabler') === 'true';

            if (isUnder18 || isDisablerOption) {
              // Disable the next button
              nextButton.disabled = true;
              nextButton.classList.add('disabled');
            } else {
              updateNextButtonState();
            }
            break;
          case 4:
          case 5:
            updateNextButtonState();
            break;
          case 6:
            quizData.goal = value;
            updateNextButtonState();
            break;
          case 7:
            quizData.occupationalPal = value;
            updateNextButtonState();
            break;
          case 8:
            quizData.exercisePal = value;
            updateNextButtonState();
            break;
          case 11:
            quizData.diet = value;
            updateNextButtonState();
            break;
        }
      }
    });
  });

  // Function to validate a step
  function validateStep(stepElement) {
    if (!stepElement) return false;

    const stepNumber = parseInt(stepElement.getAttribute('data-step'));

    // Check for different validation rules based on step number
    switch(stepNumber) {
      case 1: // Name step
        const nameInput = stepElement.querySelector('input[type="text"]');
        return nameInput && nameInput.value.trim() !== '';

      case 2: // Gender step
      case 3: // Age step
      case 6: // Goal step
      case 7: // Activity step
      case 8: // Exercise step
      case 11: // Diet step
        // For selection steps, check if an option is selected
        return stepElement.querySelector('.quiz-option-input:checked') !== null;

      case 4: // Height step
      case 5: // Weight step
        // For measurement steps, check if the input has a value
        const input = stepElement.querySelector('input');
        return input && input.value.trim() !== '';

      case 9: // Health goals step
      case 10: // Training step
        // For multi-select steps, at least one option should be selected
        return stepElement.querySelectorAll('.quiz-option-input:checked').length > 0;

      default:
        return true;
    }
  }

  function sanitizeText(input) {
    if (!input || typeof input !== 'string') return '';

    return input
            // Remove HTML brackets
            .replace(/[]/g, '')
            .replace(/([<>])/g, '')

            // Remove forward and backward slashes that could be used in closing tags
            .replace(/[\/\\]/g, '')

            // Remove JavaScript protocol
            .replace(/javascript:/gi, '')

            // Remove data: protocol (can be used for XSS)
            .replace(/data:/gi, '')

            // Remove vbscript: protocol
            .replace(/vbscript:/gi, '')

            // Remove event handlers (on*)
            .replace(/on\w+\s*=/gi, '')

            // Remove script tags (case insensitive)
            .replace(/script/gi, '')

            // Remove potentially dangerous HTML entities
            .replace(/&[#\w]+;?/g, '')

            // Remove parentheses (can be used in JavaScript execution)
            .replace(/[()]/g, '')

            // Remove square brackets (can be used in some injection contexts)
            .replace(/[\[\]]/g, '')

            // Remove curly braces (template literals, object notation)
            .replace(/[{}]/g, '')

            // Remove semicolons (JavaScript statement separators)
            .replace(/;/g, '')

            // Remove equals sign (assignment/comparison)
            .replace(/=/g, '')

            // Remove pipe symbols (can be used in some contexts)
            .replace(/\|/g, '')

            // Remove ampersand (HTML entities, URL parameters)
            .replace(/&/g, '')

            // Remove hash/pound (fragments, comments in some contexts)
            .replace(/#/g, '')

            // Trim whitespace
            .trim();
  }

  // Function to update next button state
  function updateNextButtonState() {
    if (!nextButton) return;

    const currentStepElement = document.querySelector(`.quiz-step[data-step="${currentStep}"]`);
    if (!currentStepElement) return;

    const isValid = validateStep(currentStepElement);
    nextButton.disabled = !isValid;
  }

  // Handle name input
  const nameInput = document.querySelector('.quiz-step[data-step="1"] input[type="text"]');
  if (nameInput) {
    nameInput.addEventListener('input', function(e) {
      const sanitizedValue = sanitizeText(e.target.value);
      nameInput.value = sanitizedValue;
      quizData.name = sanitizedValue;
      updateNextButtonState();
    });
  }

  // Helper function to find elements in Shadow DOM
  function findInShadowDOM(selector, shadowHostSelector = 'shopify-forms-embed') {
    const shadowHost = document.querySelector(shadowHostSelector);
    if (!shadowHost) {
      console.error(`Shadow host '${shadowHostSelector}' not found`);
      return null;
    }

    const shadowRoot = shadowHost.shadowRoot;
    if (!shadowRoot) {
      console.error(`No shadow root found for '${shadowHostSelector}'`);
      return null;
    }

    const element = shadowRoot.querySelector(selector);
    return element;
  }

  // Helper function to find elements by ID in Shadow DOM
  function findByIdInShadowDOM(id, shadowHostSelector = 'shopify-forms-embed') {
    const shadowHost = document.querySelector(shadowHostSelector);
    if (!shadowHost) {
      console.error(`Shadow host '${shadowHostSelector}' not found`);
      return null;
    }

    const shadowRoot = shadowHost.shadowRoot;
    if (!shadowRoot) {
      console.error(`No shadow root found for '${shadowHostSelector}'`);
      return null;
    }

    const element = shadowRoot.getElementById(id);
    return element;
  }

  function fillShopifyFormWithApiData(apiData) {
    // Get the shadow root
    const shadowHost = document.querySelector('shopify-forms-embed');
    if (!shadowHost) {
      console.error('Shadow host not found');
      return;
    }

    const shadowRoot = shadowHost.shadowRoot;
    if (!shadowRoot) {
      console.error('Shadow root not found');
      return;
    }

    // Direct mapping of our data to form field IDs
    const fieldMap = {
      email: 'email',
      gender: 'gender',
      ageInYears: 'age_in_years',
      heightInCm: 'height_in_cm',
      weightInKg: 'weight_in_kg',
      goal: 'goal',
      occupationalPal: 'occupational_pal',
      exercisePal: 'exercise_pal',
      needs: 'needs',
      favWorkouts: 'fav_workouts',
      diet: 'diet',
      rec_protein_in_grams: 'rec_protein_in_grams',
      rec_fat_in_grams: 'rec_fat_in_grams',
      rec_carbs_in_grams: 'rec_carbs_in_grams',
      rec_calories_in_kcal: 'rec_calories_in_kcal',
      // US range fields (grams and kcal)
      rec_protein_in_grams_low: 'rec_protein_in_grams_low',
      rec_protein_in_grams_high: 'rec_protein_in_grams_high',
      rec_carbs_in_grams_low: 'rec_carbs_in_grams_low',
      rec_carbs_in_grams_high: 'rec_carbs_in_grams_high',
      rec_fat_in_grams_low: 'rec_fat_in_grams_low',
      rec_fat_in_grams_high: 'rec_fat_in_grams_high',
      rec_calories_in_kcal_low: 'rec_calories_in_kcal_low',
      rec_calories_in_kcal_high: 'rec_calories_in_kcal_high',
      rec_protein_kcal_low: 'rec_protein_kcal_low',
      rec_protein_kcal_high: 'rec_protein_kcal_high',
      rec_carbs_kcal_low: 'rec_carbs_kcal_low',
      rec_carbs_kcal_high: 'rec_carbs_kcal_high',
      rec_fat_kcal_low: 'rec_fat_kcal_low',
      rec_fat_kcal_high: 'rec_fat_kcal_high',
      pc_trigger: 'pc_trigger',
      pc_country: 'pc_country',
      pc_locale_code: 'pc_locale_code',
      pc_language: 'pc_language',
      pc_subscribed: 'custom#pc_subscribed', // Use custom# prefix for this field
      rec_id: 'rec_id',
      macro_nutrients: 'macro_nutrients',
      // Use rec_products field for recommended products (will be stored as comma-separated string)
      recommended_products: 'rec_products'
    };

    // Add the PC trigger
    apiData.pc_trigger = "True";

    // Add the PC country
    apiData.pc_country = window.isoCode || '';
    apiData.pc_language = window.localeCode || '';
    apiData.pc_locale_code = window.langRegion || '';

    // Add pc_subscribed based on marketing checkbox state
    const marketingCheckbox = document.getElementById('marketing_checkbox');
    apiData.pc_subscribed = marketingCheckbox && marketingCheckbox.checked ? 'True' : 'False';

    // Add the email from the signup form
    const emailInput = document.getElementById('signup_email');
    if (emailInput) {
      apiData.email = emailInput.value;
    }

    // If name is present, split it into first and last names
    if (apiData.name) {
      // Split on first whitespace
      const [firstName, ...rest] = apiData.name.trim().split(' ');
      const lastName = rest.join(' ');

      // Fill first_name and last_name fields directly
      const firstNameInput = shadowRoot.getElementById('first_name');
      const lastNameInput = shadowRoot.getElementById('last_name');

      if (firstNameInput) {
        firstNameInput.value = firstName || '';

        // Trigger events to ensure the form recognizes the change
        firstNameInput.dispatchEvent(new Event('input', { bubbles: true }));
        firstNameInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (lastNameInput) {
        lastNameInput.value = lastName || '';

        lastNameInput.dispatchEvent(new Event('input', { bubbles: true }));
        lastNameInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Fill all other form fields directly
    Object.entries(fieldMap).forEach(([apiKey, fieldId]) => {
      if (apiData[apiKey] !== undefined) {
        // Check if this is a custom field (starts with custom#)
        const isCustomField = fieldId.startsWith('custom#');
        const fieldIdWithoutPrefix = isCustomField ? fieldId.replace('custom#', '') : fieldId;

        // Try different ways to find the field
        let input = null;

        // First try with the exact ID as specified in the fieldMap
        input = shadowRoot.getElementById(fieldId);

        // If not found and it's a custom field, try without the custom# prefix
        if (!input && isCustomField) {
          input = shadowRoot.getElementById(fieldIdWithoutPrefix);
        }

        // If not found and it's not a custom field, try with the custom# prefix
        if (!input && !isCustomField) {
          input = shadowRoot.getElementById('custom#' + fieldId);
        }

        // Try other selectors
        if (!input) input = shadowRoot.querySelector(`[name="${fieldId}"]`);
        if (!input) input = shadowRoot.querySelector(`[data-field-id="${fieldId}"]`);
        if (!input && isCustomField) input = shadowRoot.querySelector(`[name="${fieldIdWithoutPrefix}"]`);
        if (!input && isCustomField) input = shadowRoot.querySelector(`[data-field-id="${fieldIdWithoutPrefix}"]`);

        if (input) {
          // Set the value and trigger events
          input.value = apiData[apiKey];

          // Trigger events to ensure the form recognizes the change
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
          input.dispatchEvent(new Event('blur', { bubbles: true }));
        } else {
          console.warn(`Could not find field with ID ${fieldId} in Shadow DOM`);
        }
      }
    });

    // Return the shadow root and submit button for later use
    return {
      shadowRoot,
      submitButton: shadowRoot.querySelector('[data-testid="btn-form-submit"]') ||
                  shadowRoot.querySelector('._formSubmitButton_1nihh_77') ||
                  shadowRoot.querySelector('button[type="submit"]')
    };
  }

  // Handle measurement inputs and toggles
  // --- Loading overlay helper ---
// Show results after calculation (client-side)
function showResults(results) {
  // Build formatted results for displayResults depending on market
  let formattedResults;
  if (isUSMarket) {
    formattedResults = {
      result: {
        macros: {
          protein_grams_low: results.protein_grams_low,
          protein_grams_high: results.protein_grams_high,
          protein_kcal_low: results.protein_kcal_low,
          protein_kcal_high: results.protein_kcal_high,
          fat_grams_low: results.fat_grams_low,
          fat_grams_high: results.fat_grams_high,
          fat_kcal_low: results.fat_kcal_low,
          fat_kcal_high: results.fat_kcal_high,
          carbs_grams_low: results.carbs_grams_low,
          carbs_grams_high: results.carbs_grams_high,
          carbs_kcal_low: results.carbs_kcal_low,
          carbs_kcal_high: results.carbs_kcal_high,
          calories_kcal_estimate: results.calories_kcal_estimate || results.total_kcal,
          calories_kcal_low: results.calories_kcal_low,
          calories_kcal_high: results.calories_kcal_high
        }
      }
    };
  } else {
    formattedResults = {
      result: {
        macros: {
          protein_grams: results.protein_grams,
          fat_grams: results.fat_grams,
          carbs_grams: results.carbs_grams,
          total_kcal: results.total_kcal
        }
      }
    };
  }

  // Update visible fields
  const proteinField = document.querySelector('input[placeholder="Rec Protein In Grams"]');
  const carbsField = document.querySelector('input[placeholder="Rec Carbs In Grams"]');
  const fatField = document.querySelector('input[placeholder="Rec Fat In Grams"]');
  const caloriesField = document.querySelector('input[placeholder="Rec Calories In KCal"]');

  if (isUSMarket) {
    if (proteinField) proteinField.value = `${results.protein_grams_low || ''}–${results.protein_grams_high || ''}`;
    if (carbsField) carbsField.value = `${results.carbs_grams_low || ''}–${results.carbs_grams_high || ''}`;
    if (fatField) fatField.value = `${results.fat_grams_low || ''}–${results.fat_grams_high || ''}`;
    if (caloriesField) caloriesField.value = (results.calories_kcal_low && results.calories_kcal_high)
      ? `${results.calories_kcal_low || ''}–${results.calories_kcal_high || ''}`
      : (results.calories_kcal_estimate || results.total_kcal || '');
  } else {
    if (proteinField) proteinField.value = results.protein_grams || '';
    if (carbsField) carbsField.value = results.carbs_grams || '';
    if (fatField) fatField.value = results.fat_grams || '';
    if (caloriesField) caloriesField.value = results.total_kcal || '';
  }

  // Update hidden fields
  if (isUSMarket) {
    const hiddenProteinLow = document.querySelector('input[name="rec_protein_in_grams_low"]');
    const hiddenProteinHigh = document.querySelector('input[name="rec_protein_in_grams_high"]');
    const hiddenCarbsLow = document.querySelector('input[name="rec_carbs_in_grams_low"]');
    const hiddenCarbsHigh = document.querySelector('input[name="rec_carbs_in_grams_high"]');
    const hiddenFatLow = document.querySelector('input[name="rec_fat_in_grams_low"]');
    const hiddenFatHigh = document.querySelector('input[name="rec_fat_in_grams_high"]');
    const hiddenCaloriesEstimate = document.querySelector('input[name="rec_calories_in_kcal"]');
    const hiddenCaloriesLow = document.querySelector('input[name="rec_calories_in_kcal_low"]');
    const hiddenCaloriesHigh = document.querySelector('input[name="rec_calories_in_kcal_high"]');
    // Macro kcal range hidden fields (US only)
    const hiddenProteinKcalLow = document.querySelector('input[name="rec_protein_kcal_low"]');
    const hiddenProteinKcalHigh = document.querySelector('input[name="rec_protein_kcal_high"]');
    const hiddenCarbsKcalLow = document.querySelector('input[name="rec_carbs_kcal_low"]');
    const hiddenCarbsKcalHigh = document.querySelector('input[name="rec_carbs_kcal_high"]');
    const hiddenFatKcalLow = document.querySelector('input[name="rec_fat_kcal_low"]');
    const hiddenFatKcalHigh = document.querySelector('input[name="rec_fat_kcal_high"]');

    if (hiddenProteinLow) hiddenProteinLow.value = results.protein_grams_low || '';
    if (hiddenProteinHigh) hiddenProteinHigh.value = results.protein_grams_high || '';
    if (hiddenCarbsLow) hiddenCarbsLow.value = results.carbs_grams_low || '';
    if (hiddenCarbsHigh) hiddenCarbsHigh.value = results.carbs_grams_high || '';
    if (hiddenFatLow) hiddenFatLow.value = results.fat_grams_low || '';
    if (hiddenFatHigh) hiddenFatHigh.value = results.fat_grams_high || '';
    if (hiddenCaloriesEstimate) hiddenCaloriesEstimate.value = results.calories_kcal_estimate || results.total_kcal || '';
    if (hiddenCaloriesLow) hiddenCaloriesLow.value = results.calories_kcal_low || '';
    if (hiddenCaloriesHigh) hiddenCaloriesHigh.value = results.calories_kcal_high || '';
    if (hiddenProteinKcalLow) hiddenProteinKcalLow.value = results.protein_kcal_low || '';
    if (hiddenProteinKcalHigh) hiddenProteinKcalHigh.value = results.protein_kcal_high || '';
    if (hiddenCarbsKcalLow) hiddenCarbsKcalLow.value = results.carbs_kcal_low || '';
    if (hiddenCarbsKcalHigh) hiddenCarbsKcalHigh.value = results.carbs_kcal_high || '';
    if (hiddenFatKcalLow) hiddenFatKcalLow.value = results.fat_kcal_low || '';
    if (hiddenFatKcalHigh) hiddenFatKcalHigh.value = results.fat_kcal_high || '';
  } else {
    const hiddenProteinField = document.querySelector('input[name="rec_protein_in_grams"]');
    const hiddenCarbsField = document.querySelector('input[name="rec_carbs_in_grams"]');
    const hiddenFatField = document.querySelector('input[name="rec_fat_in_grams"]');
    const hiddenCaloriesField = document.querySelector('input[name="rec_calories_in_kcal"]');

    if (hiddenProteinField) hiddenProteinField.value = results.protein_grams || '';
    if (hiddenCarbsField) hiddenCarbsField.value = results.carbs_grams || '';
    if (hiddenFatField) hiddenFatField.value = results.fat_grams || '';
    if (hiddenCaloriesField) hiddenCaloriesField.value = results.total_kcal || '';
  }

  // Use the global quizData and name for display  
  displayResults(formattedResults, quizData, quizData && quizData.name ? quizData.name : 'User');
}

  document.querySelectorAll('.input-field').forEach(field => {
    const input = field.querySelector('input');
    const toggle = field.querySelector('.measurement-toggle');
    if (!input || !toggle) return;

    // Support for height cm select UI
    const cmSelect = field.querySelector('#height-cm-select');
    const cmSelectContainer = field.querySelector('.cm-select-container');

    if (cmSelect && input.id === 'height') {
      cmSelect.addEventListener('change', function() {
        const val = parseInt(this.value, 10) || 0;
        // Keep hidden input as canonical value holder (cm)
        input.value = val;
        input.dataset.cmValue = val;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
    }

    // Support for weight integer/decimal selects (kg/lbs)
    if (field.id === 'weight-field') {
      const kgIntSel = document.getElementById('weight-kg-int');
      const kgDecSel = document.getElementById('weight-kg-dec');
      const lbIntSel = document.getElementById('weight-lb-int');
      const kgContainer = document.getElementById('weight-kg-container');
      const lbContainer = document.getElementById('weight-lb-container');

      function updateHiddenFromKgSelects() {
        if (!kgIntSel || !kgDecSel) return;
        const kgVal = (parseInt(kgIntSel.value, 10) || 0) + ((parseInt(kgDecSel.value, 10) || 0) / 10);
        const kgClamped = clampWeight(kgVal, 'kg');
        input.value = kgClamped;
        input.dataset.kgValue = String(kgClamped);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }

      function updateHiddenFromLbSelects() {
        if (!lbIntSel) return;
        const lbVal = parseInt(lbIntSel.value, 10) || 0;
        const lbClamped = clampWeight(lbVal, 'lbs');
        input.value = lbClamped;
        // Maintain kg baseline for roundtrip
        input.dataset.kgValue = String(lbClamped / KG_TO_LBS_FACTOR);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }

      // Attach change listeners
      if (kgIntSel) kgIntSel.addEventListener('change', updateHiddenFromKgSelects);
      if (kgDecSel) kgDecSel.addEventListener('change', updateHiddenFromKgSelects);
      if (lbIntSel) lbIntSel.addEventListener('change', updateHiddenFromLbSelects);

      // Initialize based on active unit
      const activeWeightUnit = toggle.querySelector('.active')?.getAttribute('data-unit');
      if (activeWeightUnit === 'kg') {
        if (kgContainer) kgContainer.style.display = 'flex';
        if (lbContainer) lbContainer.style.display = 'none';
        updateHiddenFromKgSelects();
      } else if (activeWeightUnit === 'lbs') {
        if (kgContainer) kgContainer.style.display = 'none';
        if (lbContainer) lbContainer.style.display = 'flex';
        updateHiddenFromLbSelects();
      }
    }

    // Handle input changes
    input.addEventListener('input', function() {
      let value = this.value.trim();
      const unit = toggle.querySelector('.active').getAttribute('data-unit');
      const inputId = this.id;
      // Height input path handled below

      // Handle inches input
      if (unit === 'in' && inputId === 'height') {
        // For height in inches, we'll allow the feet and inches format (e.g., 5'6")
        // Don't modify the input directly here, as it might be in feet and inches format
        // We'll handle the parsing when storing the value
      }

      // Don't update state if the field is empty
      if (!value) {
        if (inputId === 'weight') quizData.weightInKg = null;
        if (inputId === 'height') quizData.heightInCm = null;
        updateNextButtonState();
        return;
      }

      const numValue = parseFloat(value) || 0;
      updateNextButtonState();

      if (inputId === 'height') {
        // Always store height in cm
        if (!value.trim()) {
          quizData.heightInCm = null;
        } else if (unit === 'in') {
          // For inches, the main input holds total inches
          const totalInches = numValue;
          // Convert total inches to cm and clamp to [30, 300]
          let cm = Math.round(totalInches * HEIGHT_ROUND_BY);
          if (cm < 30) cm = 30;
          if (cm > 300) cm = 300;
          quizData.heightInCm = cm;
        } else {
          // cm path (from hidden input / select). Clamp to [30, 300]
          let cm = Math.round(numValue);
          if (cm < 30) cm = 30;
          if (cm > 300) cm = 300;
          quizData.heightInCm = cm;
          // keep cm select in sync if present
          if (cmSelect && cmSelect.value !== String(cm)) {
            cmSelect.value = String(cm);
          }
        }
      } else if (inputId === 'weight') {
        // Always store weight in kg
        if (!value.trim()) {
          quizData.weightInKg = null;
        } else {
          quizData.weightInKg = unit === 'lbs' ? numValue * 0.453592 : numValue;
        }
      }
    });

    // Create or find feet and inches input fields for height
    if (input.id === 'height') {
      // Check if we already have the feet/inches container
      let feetInchesContainer = field.querySelector('.feet-inches-container');

      if (!feetInchesContainer) {
        // Create container for feet and inches inputs
        feetInchesContainer = document.createElement('div');
        feetInchesContainer.className = 'feet-inches-container';
        feetInchesContainer.style.display = 'none'; // Hidden by default

        // No JS wrapper; caret handled purely via CSS

        // Create feet dropdown
        const feetSelect = document.createElement('select');
        feetSelect.id = 'height-feet';
        feetSelect.className = 'feet-select';

        // Add feet options (1-9 ft)
        for (let i = 1; i <= 9; i++) {
          const option = document.createElement('option');
          option.value = i;
          option.textContent = i + ' ft';
          feetSelect.appendChild(option);
        }

        // Create inches dropdown
        const inchesSelect = document.createElement('select');
        inchesSelect.id = 'height-inches';
        inchesSelect.className = 'inches-select';

        // Add inches options (0-11 in)
        for (let i = 0; i <= 11; i++) {
          const option = document.createElement('option');
          option.value = i;
          option.textContent = i + ' in';
          inchesSelect.appendChild(option);
        }

        // Append raw selects; rely on CSS for caret and layout
        feetInchesContainer.appendChild(feetSelect);
        feetInchesContainer.appendChild(inchesSelect);

        // Add event listeners to update height value
        feetSelect.addEventListener('change', function() {
          updateHeightValue(field, input);
        });

        inchesSelect.addEventListener('change', function() {
          updateHeightValue(field, input);
        });

        // Insert after the main input
        input.parentNode.insertBefore(feetInchesContainer, input.nextSibling);
      }

      // Store reference to the container and inputs
      field.feetInchesContainer = feetInchesContainer;
      field.feetSelect = feetInchesContainer.querySelector('#height-feet');
      field.inchesSelect = feetInchesContainer.querySelector('#height-inches');

      // Ensure listeners are attached even if container existed (e.g., created earlier by setHeightDefaultToInches)
      if (field.feetSelect && field.inchesSelect && !feetInchesContainer.dataset.listenersAttached) {
        field.feetSelect.addEventListener('change', function() { updateHeightValue(field, input); });
        field.inchesSelect.addEventListener('change', function() { updateHeightValue(field, input); });
        feetInchesContainer.dataset.listenersAttached = '1';
      }

      // Initialize UI based on the currently active unit
      const activeUnit = toggle.querySelector('.active')?.getAttribute('data-unit');
      if (activeUnit === 'in') {
        // Ensure we have a cm baseline
        let cmBaseline = parseFloat(input.dataset.cmValue || input.value) || 0;
        if (!cmBaseline && cmSelect) {
          cmBaseline = parseInt(cmSelect.value, 10) || 0;
        }
        if (cmBaseline) {
          input.dataset.cmValue = cmBaseline;
          const totalInches = parseFloat((cmBaseline / HEIGHT_ROUND_BY).toFixed(4));
          // Compute feet/inches with minimum feet of 1
          let feet = Math.floor(totalInches / 12);
          let inches = Math.round(totalInches - feet * 12);
          if (feet < 1) { feet = 1; inches = 0; }
          if (field.feetSelect && field.inchesSelect) {
            field.feetSelect.value = String(feet);
            field.inchesSelect.value = String(inches);
          }
          // Hide cm select, show feet/inches, keep input hidden but store inches for logic
          if (cmSelectContainer) cmSelectContainer.style.display = 'none';
          feetInchesContainer.style.display = 'flex';
          input.style.display = 'none';
          input.value = totalInches;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      } else {
        // Default to cm: ensure cm select visible and input hidden
        if (cmSelectContainer) cmSelectContainer.style.display = 'block';
        feetInchesContainer.style.display = 'none';
        input.style.display = 'none';
        // Sync hidden input from cm select if present
        if (cmSelect) {
          input.value = parseInt(cmSelect.value, 10) || '';
          input.dataset.cmValue = input.value;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    }

    // Function to update height value from feet/inches inputs
    function updateHeightValue(field, mainInput) {
      if (!field.feetSelect || !field.inchesSelect) return;

      const feet = parseInt(field.feetSelect.value) || 0;
      const inches = parseInt(field.inchesSelect.value) || 0;

      // Store the total inches in the hidden main input
      const totalInches = (feet * 12) + inches;
      mainInput.value = totalInches;

      // Trigger input event to update quizData
      mainInput.dispatchEvent(new Event('input'));
    }

    // Handle unit toggle clicks
    const buttons = toggle.querySelectorAll('button');
    buttons.forEach(button => {
      button.addEventListener('click', function() {
        const currentUnit = toggle.querySelector('.active').getAttribute('data-unit');
        const newUnit = this.getAttribute('data-unit');
        if (currentUnit === newUnit) return;

        // Update active state first
        buttons.forEach(btn => btn.classList.remove('active'));
        this.classList.add('active');

        // Weight handled by dedicated listeners above; skip here
        if (input.id === 'weight') return;

        // Handle height conversion
        if (input.id === 'height') {
          const currentValue = parseFloat(input.value) || 0;

          if (currentUnit === 'cm' && newUnit === 'in') {
            // Store the original cm value for precise roundtrip conversion
            input.dataset.cmValue = currentValue;
            // Convert cm to total inches (no rounding drift)
            const totalInches = parseFloat((currentValue / HEIGHT_ROUND_BY).toFixed(4));
            // Convert to feet and inches
            let feet = Math.floor(totalInches / 12);
            let inches = Math.round((totalInches - feet * 12)); // dropdown only supports whole inches

            // Ensure feet minimum is 1
            if (feet < 1) {
              feet = 1;
              inches = 0;
            }

            // Update the feet and inches selects
            if (field.feetSelect && field.inchesSelect) {
              field.feetSelect.value = feet;
              field.inchesSelect.value = inches;

              // Show feet/inches inputs, hide main input
              input.style.display = 'none';
              field.feetInchesContainer.style.display = 'flex';
            }

            // Store total inches in the main input (hidden)
            input.value = totalInches;
            // Hide cm select when switching to inches
            if (cmSelectContainer) cmSelectContainer.style.display = 'none';
          } else if (currentUnit === 'in' && newUnit === 'cm') {
            // Get values from feet/inches inputs if they exist
            let totalInches = 0;

            if (field.feetSelect && field.inchesSelect) {
              const feet = parseInt(field.feetSelect.value) || 0;
              const inches = parseInt(field.inchesSelect.value) || 0;
              totalInches = (feet * 12) + inches;

              // Hide feet/inches inputs, show main input
              field.feetInchesContainer.style.display = 'none';
              input.style.display = 'none';
              if (cmSelectContainer) cmSelectContainer.style.display = 'block';
            } else {
              // Fallback to parsing the main input
              totalInches = currentValue;
            }

            // Always use the original cm value if available for roundtrip
            let cm = input.dataset.cmValue ? parseFloat(input.dataset.cmValue) : Math.round(totalInches * HEIGHT_ROUND_BY);
            // Clamp to [30, 300]
            cm = Math.max(30, Math.min(300, Math.round(cm)));
            input.value = cm; // display as integer in hidden input
            input.dataset.cmValue = cm; // update the stored cm value

            // Sync cm select if present
            if (cmSelect) {
              cmSelect.value = String(cm);
            }
          }

          // Trigger input event to update quizData
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
    });
  });

  // Update progress bar
  function updateProgress() {
    if (progressBarFill) {
      // Calculate progress as a percentage of completed steps
      // Start at 10% and end at 95% to indicate near completion
      const progress = currentStep === totalSteps ? 95 : 10 + ((currentStep - 1) / (totalSteps - 1)) * 85;
      progressBarFill.style.width = `${progress}%`;
    }
  }

  // Show/hide steps
  function showStep(stepNumber, animate = false) {
    // For fields with default values, trigger input event to update validation state
    const currentStepElement = document.querySelector(`.quiz-step[data-step="${stepNumber}"]`);
    if (currentStepElement) {
      const input = currentStepElement.querySelector('input[type="text"], input[type="number"]');
      if (input && input.value.trim()) {
        input.dispatchEvent(new Event('input'));
      }
    }
    updateNextButtonState();

    // Hide all steps first
    document.querySelectorAll('.quiz-step').forEach(step => {
      if (parseInt(step.getAttribute('data-step')) !== stepNumber) {
        step.style.display = 'none';
        step.classList.remove('hiding');
      }
    });

    const targetStep = document.querySelector(`.quiz-step[data-step="${stepNumber}"]`);
    if (targetStep) {
      // Always show immediately without animation
      targetStep.style.display = 'block';
    }

    if (prevButton) {
      prevButton.style.display = stepNumber === 1 ? 'none' : 'block';
    }

    if (nextButton) {
      const nextLabel = nextButton.dataset.nextLabel || nextButton.textContent || 'NEXT';
      const finishLabel = nextButton.dataset.finishLabel || nextLabel;
      nextButton.textContent = stepNumber === totalSteps ? finishLabel : nextLabel;
    }

    // Reset scroll position to the top of the quiz content
    const quizContentWrapper = document.querySelector('.quiz-content-wrapper');
    const quizContent = document.querySelector('.quiz-content');

    // Ensure both potential scroll containers are reset to top
    if (quizContentWrapper) {
      quizContentWrapper.scrollTop = 0;
    }

    if (quizContent) {
      quizContent.scrollTop = 0;
    }

    updateProgress();
  }

  // Navigation handlers
  if (prevButton) {
    prevButton.addEventListener('click', () => {
      if (window.dataLayerHelper !== undefined) {
        window.dataLayerHelper.track('P_C_PreviousClick', {
          P_C_Step_No: currentStep
        })
      }
      if (currentStep > 1) {
        const currentStepElement = document.querySelector(`.quiz-step[data-step="${currentStep}"]`);
        if (currentStepElement) {
          // Remove hiding class and timeout for instant navigation
          currentStep--;
          showStep(currentStep, false);
        }
      }
    });
  }

  if (nextButton) {
    nextButton.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (window.dataLayerHelper !== undefined) {
        window.dataLayerHelper.track('P_C_StepComplete', {
          P_C_Step_No: currentStep
        })
      }

      const currentStepElement = document.querySelector(`.quiz-step[data-step="${currentStep}"]`);
      if (!currentStepElement) {
        console.error('Current step element not found');
        return;
      }

      clearError(currentStepElement);

      // Validate current step
      if (!validateStep(currentStepElement)) {
        showError(currentStepElement);
        return;
      }

      if (currentStep < totalSteps) {
        // Remove animation for instant navigation
        currentStep++;
        showStep(currentStep, false);
        quizContentWrapper.focus();
      } else {
        document.body.style.overflow = 'auto';

        // Calculate and show results (client-side)
        if (typeof loadingOverlay !== 'undefined' && loadingOverlay) loadingOverlay.style.display = 'flex';
        const resultService = new ResultService();

        const results = resultService.calculateMacros(quizData);

        // Fetch macro nutrients and recommended products from API and append using existing logic
        // Merge rec_* values into quizData for the API request
        // Remove email from the API payload
        const { email, ...quizDataNoEmail } = quizData;
        let apiPayload = {
          ...quizDataNoEmail,
          brand: `on-${window.mainMarket}`,
          localeCode: window.localeCode
        };

        if (isUSMarket) {
            // For US market, include only high/low range values for macros and calories
            apiPayload = {
                ...apiPayload,
                rec_protein_in_grams_low: results.protein_grams_low || '',
                rec_protein_in_grams_high: results.protein_grams_high || '',
                rec_carbs_in_grams_low: results.carbs_grams_low || '',
                rec_carbs_in_grams_high: results.carbs_grams_high || '',
                rec_fat_in_grams_low: results.fat_grams_low || '',
                rec_fat_in_grams_high: results.fat_grams_high || '',
                rec_calories_kcal_low: results.calories_kcal_low || results.rec_calories_kcal_low || '',
                rec_calories_kcal_high: results.calories_kcal_high || results.rec_calories_kcal_high || ''
            };
        } else {
            // For non-US markets, use single values
            apiPayload = {
                ...apiPayload,
                rec_protein_in_grams: results.protein_grams || '',
                rec_carbs_in_grams: results.carbs_grams || '',
                rec_fat_in_grams: results.fat_grams || '',
                rec_calories_in_kcal: results.total_kcal || ''
            };
        }

        // Directly update the visible form fields with the calculated values
        const proteinField = document.querySelector('input[placeholder="Rec Protein In Grams"]');
        const carbsField = document.querySelector('input[placeholder="Rec Carbs In Grams"]');
        const fatField = document.querySelector('input[placeholder="Rec Fat In Grams"]');
        const caloriesField = document.querySelector('input[placeholder="Rec Calories In KCal"]');

        if (isUSMarket) {
          if (proteinField) proteinField.value = `${results.protein_grams_low || ''}–${results.protein_grams_high || ''}`;
          if (carbsField) carbsField.value = `${results.carbs_grams_low || ''}–${results.carbs_grams_high || ''}`;
          if (fatField) fatField.value = `${results.fat_grams_low || ''}–${results.fat_grams_high || ''}`;
          if (caloriesField) caloriesField.value = (results.calories_kcal_low && results.calories_kcal_high)
            ? `${results.calories_kcal_low || ''}–${results.calories_kcal_high || ''}`
            : (results.calories_kcal_estimate || results.total_kcal || '');

          // Hidden fields for US ranges
          const hiddenProteinLow = document.querySelector('input[name="rec_protein_in_grams_low"]');
          const hiddenProteinHigh = document.querySelector('input[name="rec_protein_in_grams_high"]');
          const hiddenCarbsLow = document.querySelector('input[name="rec_carbs_in_grams_low"]');
          const hiddenCarbsHigh = document.querySelector('input[name="rec_carbs_in_grams_high"]');
          const hiddenFatLow = document.querySelector('input[name="rec_fat_in_grams_low"]');
          const hiddenFatHigh = document.querySelector('input[name="rec_fat_in_grams_high"]');
          const hiddenCaloriesEstimate = document.querySelector('input[name="rec_calories_in_kcal"]');
          const hiddenCaloriesLow = document.querySelector('input[name="rec_calories_in_kcal_low"]');
          const hiddenCaloriesHigh = document.querySelector('input[name="rec_calories_in_kcal_high"]');
          // Macro kcal range hidden fields (US only)
          const hiddenProteinKcalLow = document.querySelector('input[name="rec_protein_kcal_low"]');
          const hiddenProteinKcalHigh = document.querySelector('input[name="rec_protein_kcal_high"]');
          const hiddenCarbsKcalLow = document.querySelector('input[name="rec_carbs_kcal_low"]');
          const hiddenCarbsKcalHigh = document.querySelector('input[name="rec_carbs_kcal_high"]');
          const hiddenFatKcalLow = document.querySelector('input[name="rec_fat_kcal_low"]');
          const hiddenFatKcalHigh = document.querySelector('input[name="rec_fat_kcal_high"]');

          if (hiddenProteinLow) hiddenProteinLow.value = results.protein_grams_low || '';
          if (hiddenProteinHigh) hiddenProteinHigh.value = results.protein_grams_high || '';
          if (hiddenCarbsLow) hiddenCarbsLow.value = results.carbs_grams_low || '';
          if (hiddenCarbsHigh) hiddenCarbsHigh.value = results.carbs_grams_high || '';
          if (hiddenFatLow) hiddenFatLow.value = results.fat_grams_low || '';
          if (hiddenFatHigh) hiddenFatHigh.value = results.fat_grams_high || '';
          if (hiddenCaloriesEstimate) hiddenCaloriesEstimate.value = results.calories_kcal_estimate || results.total_kcal || '';
          if (hiddenCaloriesLow) hiddenCaloriesLow.value = results.calories_kcal_low || '';
          if (hiddenCaloriesHigh) hiddenCaloriesHigh.value = results.calories_kcal_high || '';
          if (hiddenProteinKcalLow) hiddenProteinKcalLow.value = results.protein_kcal_low || '';
          if (hiddenProteinKcalHigh) hiddenProteinKcalHigh.value = results.protein_kcal_high || '';
          if (hiddenCarbsKcalLow) hiddenCarbsKcalLow.value = results.carbs_kcal_low || '';
          if (hiddenCarbsKcalHigh) hiddenCarbsKcalHigh.value = results.carbs_kcal_high || '';
          if (hiddenFatKcalLow) hiddenFatKcalLow.value = results.fat_kcal_low || '';
          if (hiddenFatKcalHigh) hiddenFatKcalHigh.value = results.fat_kcal_high || '';
        } else {
          if (proteinField) proteinField.value = results.protein_grams || '';
          if (carbsField) carbsField.value = results.carbs_grams || '';
          if (fatField) fatField.value = results.fat_grams || '';
          if (caloriesField) caloriesField.value = results.total_kcal || '';

          // Hidden fields for non-US singles
          const hiddenProteinField = document.querySelector('input[name="rec_protein_in_grams"]');
          const hiddenCarbsField = document.querySelector('input[name="rec_carbs_in_grams"]');
          const hiddenFatField = document.querySelector('input[name="rec_fat_in_grams"]');
          const hiddenCaloriesField = document.querySelector('input[name="rec_calories_in_kcal"]');

          if (hiddenProteinField) hiddenProteinField.value = results.protein_grams || '';
          if (hiddenCarbsField) hiddenCarbsField.value = results.carbs_grams || '';
          if (hiddenFatField) hiddenFatField.value = results.fat_grams || '';
          if (hiddenCaloriesField) hiddenCaloriesField.value = results.total_kcal || '';
        }
        fetch(pcApiEndpoint + '/api/result/getAdditionalData', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(apiPayload)
        })
        .then(response => response.json())
        .then(apiResults => {
          // Ensure result object exists
          if (!apiResults.result) {
            apiResults.result = {};
          }

          // Patch: Ensure macros object exists for displayResults
          if (!apiResults.result.macros) {
            apiResults.result.macros = results;
          }

          // Handle recommended products - check all possible locations
          // 1. First try rec_products in the result object
          if (apiResults.result.rec_products) {
            if (typeof apiResults.result.rec_products === 'string') {
              try {
                // Check if it looks like a JSON string
                if (apiResults.result.rec_products.startsWith('[') && apiResults.result.rec_products.endsWith(']')) {
                  // Try to parse it as JSON array
                  const parsed = JSON.parse(apiResults.result.rec_products);
                  // Make sure we have an array of strings, not an array as a string
                  if (Array.isArray(parsed)) {
                    apiResults.result.recommended_products = parsed;
                  } else {
                    // If it parsed but isn't an array, use as single item
                    apiResults.result.recommended_products = [parsed];
                  }
                } else if (apiResults.result.rec_products.includes(',')) {
                  // Might be a comma-separated list
                  apiResults.result.recommended_products = apiResults.result.rec_products.split(',').map(p => p.trim());
                } else {
                  // Might be a single product handle
                  apiResults.result.recommended_products = [apiResults.result.rec_products];
                }
              } catch (e) {
                console.error('Error processing rec_products string:', e);
                // Fallback to using it as a single product handle
                apiResults.result.recommended_products = [apiResults.result.rec_products];
              }
            } else if (Array.isArray(apiResults.result.rec_products)) {
              // Make sure each item is a string, not an array or object
              apiResults.result.recommended_products = apiResults.result.rec_products.map(item => {
                if (typeof item === 'string') return item;
                if (item && typeof item === 'object') {
                  if (item.handle) return item.handle;
                  if (item.product_handle) return item.product_handle;
                  if (item.id) return item.id;
                }
                return '';
              }).filter(item => item !== '');
            }
          }
          // 2. Try recommended_products at the top level
          else if (apiResults.recommended_products) {
            if (typeof apiResults.recommended_products === 'string') {
              try {
                apiResults.result.recommended_products = JSON.parse(apiResults.recommended_products);
              } catch (e) {
                // Try to use it as a direct string if parsing fails
                if (apiResults.recommended_products.includes(',')) {
                  apiResults.result.recommended_products = apiResults.recommended_products.split(',').map(p => p.trim());
                } else {
                  apiResults.result.recommended_products = [apiResults.recommended_products];
                }
              }
            } else if (Array.isArray(apiResults.recommended_products)) {
              apiResults.result.recommended_products = apiResults.recommended_products;
            }
          }

          const sanitizedName = sanitizeText(quizData?.name ?? 'User');

          displayResults(apiResults, quizData, sanitizedName);
        })
        .catch(error => {
          console.error('Error fetching additional macro/product data:', error);
          // Fallback to client-calculated results only
          showResults(results);
        })
        .finally(() => {
          if (typeof loadingOverlay !== 'undefined' && loadingOverlay) loadingOverlay.style.display = 'none';
          // Clear the session storage after displaying results
          sessionStorage.removeItem('proteinCalculatorResults');
        });
      }
    });
  }

  // (Removed duplicate openButton handler; handled above with initQuiz + tracking)

  // Function to completely reset the quiz state
  function resetQuiz() {
    // Reset to first step
    currentStep = 1;
    showStep(currentStep);

    // Reset all selections
    document.querySelectorAll('.quiz-option-input').forEach(opt => opt.classList.remove('selected'));

    // Reset all text inputs
    document.querySelectorAll('.quiz-step input[type="text"], .quiz-step input[type="number"]').forEach(input => {
      input.value = '';
    });

    // Reset measurement toggles to default state
    document.querySelectorAll('.measurement-toggle button').forEach(button => {
      if (button.classList.contains('default-unit')) {
        // Activate default unit buttons
        const toggle = button.closest('.measurement-toggle');
        if (toggle) {
          toggle.querySelectorAll('button').forEach(btn => btn.classList.remove('active'));
          button.classList.add('active');
        }
      }
    });

    // Reset feet/inches inputs if they exist
    const feetSelect = document.getElementById('height-feet');
    const inchesSelect = document.getElementById('height-inches');
    if (feetSelect) feetSelect.selectedIndex = 0;
    if (inchesSelect) inchesSelect.selectedIndex = 0;

    // Reset any visible feet/inches container
    const feetInchesContainer = document.querySelector('.feet-inches-container');
    if (feetInchesContainer) {
      feetInchesContainer.style.display = 'none';
    }

    // Ensure hidden input stays hidden and cm select is visible
    const heightInput = document.getElementById('height');
    if (heightInput) {
      heightInput.style.display = 'none';
    }
    const cmSelectContainerReset = document.querySelector('.cm-select-container');
    if (cmSelectContainerReset) {
      cmSelectContainerReset.style.display = 'block';
    }
    const cmSelectReset = document.getElementById('height-cm-select');
    if (cmSelectReset && heightInput) {
      const selectedOption = cmSelectReset.querySelector('option[selected]');
      if (selectedOption) {
        cmSelectReset.value = selectedOption.value;
      } else {
        cmSelectReset.selectedIndex = 0;
      }
      heightInput.value = parseInt(cmSelectReset.value, 10) || '';
      heightInput.dataset.cmValue = heightInput.value;
      heightInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Reset all quiz data
    quizData = {
      name: '',
      gender: '',
      ageInYears: '',
      heightInCm: '',
      weightInKg: '',
      goal: '',
      occupationalPal: '',
      exercisePal: '',
      needs: [],
      favWorkouts: [],
      diet: '',
      healthGoalsSelected: 0,
      trainingTypesSelected: 0
    };

    // Reset next button state
    if (nextButton) {
      nextButton.disabled = true;
    }

    // Reset progress bar
    updateProgress();

    console.log('Quiz has been reset');
  }

  if (closeButton) {
    closeButton.addEventListener('click', () => {
      if (modal) {
        if (window.dataLayerHelper !== undefined) {
          window.dataLayerHelper.track('P_C_Close', {
            P_C_Step_No: currentStep
          });
        }
        modal.style.display = 'none';
        // Reset body overflow to allow scrolling
        document.body.style.overflow = 'auto';
        // Reset the quiz completely
        resetQuiz();
      }
    });
  }

  function initSignUpForm() {
    const form = document.querySelector('.signup-form');
    if (!form) return;

    // Check if rec_id is in the URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const recId = urlParams.get('rec_id');

    // Only show the form if there's no rec_id
    if (recId) {
      form.style.display = 'none';
    } else {
      form.style.display = 'block';
    }

    // Add spinner style
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      .submit-button-loading {
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: not-allowed;
        opacity: 0.7;
      }
    `;
    document.head.appendChild(style);

    // Create spinner HTML with text
    const spinnerHTML = `<div style="display: flex; align-items: center; justify-content: center; gap: 10px;">
      <div class="submit-spinner" style="display: inline-block; width: 24px; height: 24px; border: 3px solid rgba(255,255,255,0.3); border-radius: 50%; border-top-color: #fff; animation: spin 1s ease-in-out infinite;"></div>
      <span>${window.proteinCalcI18n.submitting}</span>
    </div>`;

    // Find the submit button in the form
    const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
    let originalButtonText = '';
    let originalButtonWidth = 0;

    if (submitButton) {
      // Store the original button text/content
      originalButtonText = submitButton.innerHTML;
      originalButtonWidth = submitButton.offsetWidth;
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      if (window.isSubmitting) {
        return;
      }

      const marketingInputCb = form.querySelector('#marketing_checkbox');
      const marketingInputHidden = form.querySelector('#marketing_checkbox_hidden'); // this checkbox is needed for Shopify pixel tracking
      if (marketingInputHidden !== null) {
        marketingInputHidden.value = String(marketingInputCb.checked);
      }

      window.isSubmitting = true;

      const email = document.getElementById('signup_email').value;
      if (!email) return;

      // Validate all required fields before submission
      const requiredFields = ['gender', 'ageInYears', 'heightInCm', 'weightInKg', 'goal', 'occupationalPal', 'exercisePal'];
      const missingFields = [];

      requiredFields.forEach(field => {
        if (!quizData[field]) {
          missingFields.push(field);
        }
      });

      if (missingFields.length > 0) {
        console.error('Missing required fields:', missingFields);
        alert('Please complete all required fields in the calculator before submitting.');
        return;
      }

      // Show spinner on submit button if it exists
      if (submitButton) {
        // Save original width to prevent button from changing size
        submitButton.style.minWidth = originalButtonWidth + 'px';
        submitButton.innerHTML = spinnerHTML;
        submitButton.classList.add('submit-button-loading');
        submitButton.disabled = true;
      }

      try {
        // Get API data from window.proteinCalculatorApiData if available
        const apiData = window.proteinCalculatorApiData || {};

        // Create a complete data object with both quiz data and API data
        const completeData = { ...quizData };

        // Add API data if available
        if (apiData.result) {
          // Add macro nutrients as JSON string if available
          if (apiData.result.macro_nutrients) {
            completeData.macro_nutrients = JSON.stringify(apiData.result.macro_nutrients);
          }

          // Add recommended products as comma-separated string if available
          if (apiData.result.recommended_products) {
            // Convert array to comma-separated string
            if (Array.isArray(apiData.result.recommended_products)) {
              completeData.recommended_products = apiData.result.recommended_products.join(',');
            } else if (typeof apiData.result.recommended_products === 'string') {
              // If it's already a string, check if it's JSON
              if (apiData.result.recommended_products.startsWith('[') && apiData.result.recommended_products.endsWith(']')) {
                try {
                  const productsArray = JSON.parse(apiData.result.recommended_products);
                  completeData.recommended_products = productsArray.join(',');
                } catch (e) {
                  // If parsing fails, use as is (might already be comma-separated)
                  completeData.recommended_products = apiData.result.recommended_products;
                }
              } else {
                // Already a string, use as is
                completeData.recommended_products = apiData.result.recommended_products;
              }
            }
          }

          // Add rec_id if available
          if (apiData.result.rec_id) {
            completeData.rec_id = apiData.result.rec_id;
          }

          // Add macros data
          if (apiData.result.macros) {
            // Use the property names from the updated ResultService
            completeData.rec_protein_in_grams = apiData.result.macros.protein_grams;
            completeData.rec_carbs_in_grams = apiData.result.macros.carbs_grams;
            completeData.rec_fat_in_grams = apiData.result.macros.fat_grams;
            completeData.rec_calories_in_kcal = apiData.result.macros.total_kcal;

            // Directly update the visible form fields
            const proteinField = document.querySelector('input[placeholder="Rec Protein In Grams"]');
            const carbsField = document.querySelector('input[placeholder="Rec Carbs In Grams"]');
            const fatField = document.querySelector('input[placeholder="Rec Fat In Grams"]');
            const caloriesField = document.querySelector('input[placeholder="Rec Calories In KCal"]');

            if (proteinField) proteinField.value = apiData.result.macros.protein_grams;
            if (carbsField) carbsField.value = apiData.result.macros.carbs_grams;
            if (fatField) fatField.value = apiData.result.macros.fat_grams;
            if (caloriesField) caloriesField.value = apiData.result.macros.total_kcal;
          }

          // Include US range values in submission payload
          if (isUSMarket && apiData.result && apiData.result.macros) {
            const m = apiData.result.macros;
            completeData.rec_protein_in_grams_low = m.protein_grams_low || '';
            completeData.rec_protein_in_grams_high = m.protein_grams_high || '';
            completeData.rec_carbs_in_grams_low = m.carbs_grams_low || '';
            completeData.rec_carbs_in_grams_high = m.carbs_grams_high || '';
            completeData.rec_fat_in_grams_low = m.fat_grams_low || '';
            completeData.rec_fat_in_grams_high = m.fat_grams_high || '';
            completeData.rec_calories_in_kcal_low = m.calories_kcal_low || '';
            completeData.rec_calories_in_kcal_high = m.calories_kcal_high || '';
            completeData.rec_protein_kcal_low = m.protein_kcal_low || '';
            completeData.rec_protein_kcal_high = m.protein_kcal_high || '';
            completeData.rec_carbs_kcal_low = m.carbs_kcal_low || '';
            completeData.rec_carbs_kcal_high = m.carbs_kcal_high || '';
            completeData.rec_fat_kcal_low = m.fat_kcal_low || '';
            completeData.rec_fat_kcal_high = m.fat_kcal_high || '';
          }
        }

        // Fill the form fields and get the form elements
        const formResult = fillShopifyFormWithApiData(completeData);

        if (!formResult) {
          console.error('Failed to fill form - shadow root not found');
          return;
        }

        const { shadowRoot, submitButton } = formResult;

        if (submitButton) {
          // Now click the submit button
          submitButton.click();

          // Show success message
          setTimeout(() => {
            const signupForm = document.querySelector('#signup_form');
            const formTitle = document.querySelector('.signup-form-title');
            const successMessage = document.querySelector('.report-success-message');

            if (signupForm) signupForm.style.display = 'none';
            if (formTitle) formTitle.style.display = 'none';
            if (successMessage) successMessage.style.display = 'block';

          }, 500); // Small delay to ensure form submission completes
        } else {
          console.error('No submit button found in the Shadow DOM');

          // Last resort: try to find any submit button directly
          const anySubmitBtn = shadowRoot.querySelector('button[type="submit"]');
          if (anySubmitBtn) {
            anySubmitBtn.click();

            // Show success message
            setTimeout(() => {
              const signupForm = document.querySelector('#signup_form');
              const formTitle = document.querySelector('.signup-form-title');
              const successMessage = document.querySelector('.report-success-message');

              if (signupForm) signupForm.style.display = 'none';
              if (formTitle) formTitle.style.display = 'none';
              if (successMessage) successMessage.style.display = 'block';
            }, 500);
          } else {
            console.error('All attempts to find a submit button failed');
          }
        }
      } catch (error) {
        console.error('Error:', error);
      }
    });
  }

  // Initialize the quiz
  function initQuiz() {
    showStep(1);
  }

// Function to process sales points in product cards for protein calculator
function processProductSalesPoints() {
  // Fix product titles to ensure they display fully without breaking the slider
  setTimeout(() => {
    // Use setTimeout to ensure this runs after the slider is initialized
    const productTitles = document.querySelectorAll('.protein-calculator-product .card__heading a');
    productTitles.forEach(title => {
      // Make title text slightly smaller and ensure it doesn't wrap
      title.style.fontSize = '14px';
      title.style.lineHeight = '1.2';
      title.style.maxWidth = 'none';
      title.style.width = 'auto';

      // Get the actual text content
      const fullText = title.textContent;

      // If the text is too long (like 'Perfection Bar Deluxe'), ensure it's visible
      if (fullText.length > 15) {
        title.style.whiteSpace = 'nowrap';
        title.style.overflow = 'visible';
        title.style.position = 'relative';
        title.style.zIndex = '1';
      }
    });
  }, 500);

  // Find all product cards with sales points
  const salesPointsContainers = document.querySelectorAll('.protein-calculator-product .card__sales-points');
  salesPointsContainers.forEach(container => {
    // Get the text content which might contain the JSON array
    const content = container.textContent.trim();
    // Check if it looks like a JSON array
    if (content.includes('[') && content.includes(']')) {
      try {
        // Extract the array part
        let jsonText = content.substring(content.indexOf('['), content.lastIndexOf(']') + 1);

        // Try to parse as JSON, or fall back to manual parsing
        let points = [];
        try {
          points = JSON.parse(jsonText);
        } catch (e) {
          // Manual parsing as fallback
          jsonText = jsonText.replace(/\[|\]/g, ''); // Remove brackets
          points = jsonText.split(',').map(point => {
            return point.replace(/\\"|\"|\'|\s+/g, ' ').trim(); // Remove quotes and trim
          }).filter(point => point !== ''); // Remove empty points
        }

        // Clear the container
        container.innerHTML = '';

        // Add styling
        const style = document.createElement('style');
        style.textContent = `
          .card__sales-points {
            font-size: 14px;
            font-weight: 500;
            line-height: 1.4;
          }
          .ksp-check {
            width: 16px;
            height: 16px;
            min-width: 16px;
            min-height: 16px;
            margin-right: 10px;
            color: #00b67a;
          }
        `;
        container.appendChild(style);

        // Create new formatted sales points
        points.forEach(point => {
          if (point && typeof point === 'string' && point.trim()) {
            const pointDiv = document.createElement('div');
            pointDiv.className = 'flex items-start gap-2';

            pointDiv.innerHTML = `
              <svg class="ksp-check mt-1 flex-shrink-0 text-green-600" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span class="flex-1">${point.trim()}</span>
            `;

            container.appendChild(pointDiv);
          }
        });
      } catch (e) {
        console.error('Error processing sales points:', e);
      }
    }
  });
}

async function fetchResultsByRecId(recId, customerId) {
  try {
    const isoCode = window.isoCode;

    // Call API to get results by rec_id
    const response = await fetch(`${pcApiEndpoint}/api/result/${recId}?brand=on-${isoCode}&customerId=${customerId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });


    // Check if response is ok (status code 200-299)
    if (!response.ok) {
      console.error('Failed to fetch results:', response.status);
      // If not successful, hide loading overlay and show normal quiz
      const loadingOverlay = document.querySelector('.loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.style.display = 'none';
      }

      // Show quiz content and navigation
      const quizContent = document.querySelector('.quiz-content');
      const quizContentWrapper = document.querySelector('.quiz-content-wrapper');
      const quizNavigation = document.querySelector('.quiz-navigation');
      const progressBar = document.querySelector('.progress-bar');

      if (quizContent) quizContent.style.display = 'block';
      if (quizContentWrapper) quizContentWrapper.style.display = 'block';
      if (quizNavigation) quizNavigation.style.display = 'flex';
      if (progressBar) progressBar.style.display = 'block';

      // Initialize the quiz as normal
      initQuiz();
      return;
    }

    // Parse the response JSON
    const results = await response.json();
    // Format the data to match the structure expected by displayResults
    const formattedResults = {
      result: {
        macros: {
          protein_grams: results.rec_protein_in_grams || '',
          carbs_grams: results.rec_carbs_in_grams || '',
          fat_grams: results.rec_fat_in_grams || '',
          total_kcal: results.rec_calories_in_kcal || results.calories_kcal_estimate || '',
          protein_grams_low: results.rec_protein_in_grams_low || '',
          protein_grams_high: results.rec_protein_in_grams_high || '',
          carbs_grams_low: results.rec_carbs_in_grams_low || '',
          carbs_grams_high: results.rec_carbs_in_grams_high || '',
          fat_grams_low: results.rec_fat_in_grams_low || '',
          fat_grams_high: results.rec_fat_in_grams_high || '',
          calories_kcal_low: results.rec_calories_in_kcal_low || '',
          calories_kcal_high: results.rec_calories_in_kcal_high || '',
          macro_nutrients: results.macro_nutrients || []
        },
        items: results.macro_nutrients || [],
        recommended_products: Array.isArray(results.rec_products) ? results.rec_products :
                             (typeof results.rec_products === 'string' ? JSON.parse(results.rec_products) : [])
      }
    };

    // Update the global quizData object with the user's information from API
    quizData.gender = results.gender || '';
    quizData.ageInYears = results.age_in_years || '';
    quizData.heightInCm = results.height_in_cm || '';
    quizData.weightInKg = results.weight_in_kg || '';
    quizData.goal = results.goal || '';
    quizData.occupationalPal = results.occupational_pal || '';
    quizData.exercisePal = results.exercise_pal || '';
    quizData.needs = results.needs || '';
    quizData.favWorkouts = results.fav_workouts || '';
    quizData.diet = results.diet || '';
    quizData.email = results.email || '';
    quizData.name = results.name || (results.firstName && results.lastName ? `${results.firstName} ${results.lastName}` : results.firstName || '');
    quizData.rec_id = results.rec_id || '';
    quizData.macro_nutrients = results.macro_nutrients ? JSON.stringify(results.macro_nutrients) : '';
    quizData.recommended_products = results.rec_products ? JSON.stringify(results.rec_products) : '';

    // Display the results using the existing function
    // Use name property instead of email for the heading
    displayResults(formattedResults, quizData, results.name || results.firstName || 'User');

    // Ensure signup form remains hidden when using rec_id
    const signupForm = document.querySelector('.signup-form');
    if (signupForm) signupForm.style.display = 'none';

  } catch (error) {
    console.error('Error fetching results by rec_id:', error);
    // If there's an error, hide loading overlay and show normal quiz
    const loadingOverlay = document.querySelector('.loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.style.display = 'none';
      // Reset body overflow to allow scrolling
      document.body.style.overflow = 'auto';
    }

    // Show quiz content and navigation
    const quizContent = document.querySelector('.quiz-content');
    const quizContentWrapper = document.querySelector('.quiz-content-wrapper');
    const quizNavigation = document.querySelector('.quiz-navigation');
    const progressBar = document.querySelector('.progress-bar');

    if (quizContent) quizContent.style.display = 'block';
    if (quizContentWrapper) quizContentWrapper.style.display = 'block';
    if (quizNavigation) quizNavigation.style.display = 'flex';
    if (progressBar) progressBar.style.display = 'block';

    // Initialize the quiz as normal
    initQuiz();
  }
}

async function fetchRecommendedProducts(handles) {
  // Handle different input formats
  let productHandles = [];

  // If handles is a string (comma-separated), split it
  if (typeof handles === 'string') {
    productHandles = handles.split(',').map(h => h.trim()).filter(h => h !== '');
  }
  // If handles is an array, use it directly
  else if (Array.isArray(handles) && handles.length > 0) {
    productHandles = handles;
  }
  // If no valid handles, return empty string
  else {
    console.error('No valid product handles provided');
    return '';
  }

  // Clean and normalize handles - this is critical
  const normalizedHandles = productHandles.map(handle => {
    if (!handle) return '';

    // If handle is not a string, try to convert it
    let handleStr = typeof handle === 'string' ? handle : String(handle);

    // Remove any surrounding quotes, brackets, or whitespace
    handleStr = handleStr.trim().replace(/^["\[\s]+|["\]\s]+$/g, '');

    // Remove any invalid URL characters
    handleStr = handleStr.replace(/[\"\[\]{}|\\^~`<>]/g, '');

    return handleStr;
  });

  // Filter out any empty handles
  const validHandles = normalizedHandles.filter(handle => handle && handle.trim() !== '');

  if (validHandles.length === 0) {
    console.error('No valid product handles after filtering');
    return '';
  }

  const productListItems = await Promise.all(
    validHandles.map(async (handle) => {
      try {
        const currentMarketUrl = window.location.href.split('/pages')[0]; //
        // Add protein_calculator=true parameter to indicate these products are for the protein calculator
        const url = `${currentMarketUrl}/products/${handle}?view=card`;

        const response = await fetch(url);
        if (!response.ok) {
          console.error(`Failed to fetch product: ${handle}, status: ${response.status}`);
          return '';
        }

        let productHtml = await response.text();
        // Product HTML is processed by Liquid template

        // Add a custom class to identify protein calculator products for styling
        return `<div class="slider__item protein-calculator-product cc-animate-init cc-animate-in">${productHtml}</div>`;
      } catch (error) {
        console.error('Error fetching product:', handle, error);
        return '';
      }
    })
  );

  const combinedHTML = productListItems.join('');
  return combinedHTML;
}

// Dotter script is now expected to be globally included on the Protein Calculator page for non-transactional markets.

// Initialize Dotter embeds within product cards (Protein Calculator recommended products)
function initDotterCardEmbeds() {
  const run = () => {
    if (!window.Dotter) return;
    const containers = document.querySelectorAll('.dotter-card-embed__container');
    if (!containers || containers.length === 0) return;

    containers.forEach(container => {
      try {
        if (container.dataset.dotterInitialized === '1') return;
        const productName = container.dataset.productName || '';
        if (!productName) return;

        const options = {
          feed: {
            filter: { name: [productName] },
            selectedProduct: { productId: productName }
          },
          footer: { hide: true }
        };

        const dotterApp = new Dotter(options);
        dotterApp.embed(container);
        container.dataset.dotterInitialized = '1';
      } catch (e) {
        console.error('Dotter init error:', e);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
}

// Function to display results from either quiz calculation or rec_id fetch
async function displayResults(results, quizData, firstName) {
  try {
    if (window.dataLayerHelper !== undefined) {
      window.dataLayerHelper.track('P_C_Results_page')
    }

    // Store the API data in a global variable for later use in form submission
    window.proteinCalculatorApiData = results;

    // Always ensure body overflow is reset to allow scrolling when viewing results
    document.body.style.overflow = 'auto';

      // Create a reference to apiData for compatibility with both direct API calls and rec_id loading
    const apiData = results;

    // Get additional elements not in global scope
    let resultsPanel = document.querySelector('.macro-calculator-results-panel');
    let loadingElement = document.querySelector('.macro-calculator-results-panel__loading');
    let errorElement = document.querySelector('.macro-calculator-results-panel__error');
    let contentElement = document.querySelector('.macro-calculator-results-panel__content');

    // Show the results panel when calculation starts
    if (resultsPanel) resultsPanel.style.display = 'block';

    // Only check for essential elements and create others if needed
    if (!resultsContent || !quizContent) {
      console.error('Essential elements not found');
      return;
    }

    // Create elements if they don't exist
    if (!loadingElement) {
      loadingElement = document.querySelector('.loading-overlay');
    }

    if (!errorElement) {
      errorElement = document.querySelector('.macro-calculator-results-panel__error');
      if (!errorElement && resultsPanel) {
        errorElement = document.createElement('div');
        errorElement.className = 'macro-calculator-results-panel__error';
        errorElement.innerHTML = `<p>${window.proteinCalcSettings?.errorMessage || window.proteinCalcI18n.genericError}</p>`;
        resultsPanel.appendChild(errorElement);
      }
    }

    if (!contentElement) {
      contentElement = document.querySelector('.macro-calculator-results-panel__content');
      if (!contentElement && resultsPanel) {
        contentElement = document.createElement('div');
        contentElement.className = 'macro-calculator-results-panel__content';
        resultsPanel.appendChild(contentElement);
      }
    }

    // Hide error and content elements
    if (errorElement) errorElement.style.display = 'none';
    if (contentElement) contentElement.style.display = 'none';

    // Extract the macros and recommended products from the result object
    // Make sure we're using the correct data structure regardless of how it was loaded
    const macros = results.result ? results.result.macros : (results.macros || {});
    // Store the items for use in the macro nutrients section
    const macroNutrientItems = results.result.macro_nutrients || results.result.items;

    // Handle different formats of recommended products
    let recommendedProducts = [];

    if (results.result) {
      if (Array.isArray(results.result.recommended_products)) {
        recommendedProducts = results.result.recommended_products;
      } else if (typeof results.result.recommended_products === 'string') {
        // Check if it's a comma-separated string
        if (results.result.recommended_products.includes(',')) {
          recommendedProducts = results.result.recommended_products.split(',').map(p => p.trim());
        } else if (results.result.recommended_products.startsWith('[') && results.result.recommended_products.endsWith(']')) {
          // Try to parse as JSON if it looks like JSON
          try {
            recommendedProducts = JSON.parse(results.result.recommended_products);
          } catch (e) {
            console.error('Error parsing recommended_products string:', e);
            // If parsing fails, treat as a single item
            recommendedProducts = [results.result.recommended_products];
          }
        } else {
          // Single item string
          recommendedProducts = [results.result.recommended_products];
        }
      } else if (results.result.rec_products) {
        // Try the rec_products field if recommended_products is not available
        if (Array.isArray(results.result.rec_products)) {
          recommendedProducts = results.result.rec_products;
        } else if (typeof results.result.rec_products === 'string') {
          // Check if it's a comma-separated string
          if (results.result.rec_products.includes(',')) {
            recommendedProducts = results.result.rec_products.split(',').map(p => p.trim());
          } else if (results.result.rec_products.startsWith('[') && results.result.rec_products.endsWith(']')) {
            // Try to parse as JSON if it looks like JSON
            try {
              recommendedProducts = JSON.parse(results.result.rec_products);
            } catch (e) {
              console.error('Error parsing rec_products string:', e);
              // If parsing fails, treat as a single item
              recommendedProducts = [results.result.rec_products];
            }
          } else {
            // Single item string
            recommendedProducts = [results.result.rec_products];
          }
        }
      }
    } else if (Array.isArray(results.recommended_products)) {
      recommendedProducts = results.recommended_products;
    } else if (results.rec_products) {
      if (Array.isArray(results.rec_products)) {
        recommendedProducts = results.rec_products;
      } else if (typeof results.rec_products === 'string') {
        // Check if it's a comma-separated string
        if (results.rec_products.includes(',')) {
          recommendedProducts = results.rec_products.split(',').map(p => p.trim());
        } else if (results.rec_products.startsWith('[') && results.rec_products.endsWith(']')) {
          // Try to parse as JSON if it looks like JSON
          try {
            recommendedProducts = JSON.parse(results.rec_products);
          } catch (e) {
            console.error('Error parsing results.rec_products string:', e);
            // If parsing fails, treat as a single item
            recommendedProducts = [results.rec_products];
          }
        } else {
          // Single item string
          recommendedProducts = [results.rec_products];
        }
      }
    }

    // Prepare display values for macros (US shows ranges)
    const proteinDisplay = ((macros.protein_grams_low || macros.protein_grams_high))
      ? `${macros.protein_grams_low || ''}–${macros.protein_grams_high || ''}`
      : `${macros.protein_grams || ''}`;

    const carbsDisplay = ((macros.carbs_grams_low || macros.carbs_grams_high))
      ? `${macros.carbs_grams_low || ''}–${macros.carbs_grams_high || ''}`
      : `${macros.carbs_grams || ''}`;

    const fatDisplay = ((macros.fat_grams_low || macros.fat_grams_high))
      ? `${macros.fat_grams_low || ''}–${macros.fat_grams_high || ''}`
      : `${macros.fat_grams || ''}`;

    const caloriesDisplay = ((macros.calories_kcal_low || macros.calories_kcal_high))
      ? `${macros.calories_kcal_low || ''}–${macros.calories_kcal_high || ''}`
      : `${macros.total_kcal || macros.calories_kcal_estimate || ''}`;

    // Create results HTML
    const resultsHTML = `
        <div data-section-type="protein-calculator-results">
          <div class="container">
            <div class="macro-calculator-results__intro">
              <h1 class="macro-calculator-results__intro-heading">
                ${ window.proteinCalcSettings.resultsHeadingTemplate.replace(/\{\s*name\s*\}/gi, `${firstName.trim()}`) }
              </h1>
              <div class="macro-calculator-results__intro-copy">
                ${window.proteinCalcSettings?.resultsIntroCopy}
              </div>
            </div>

            <div class="macro-calculator-results-panel">
              <div class="macro-calculator-results-panel__heading">${window.proteinCalcSettings?.resultsPanelHeading}</div>
              <div class="macro-calculator-results-panel__subheading">${window.proteinCalcSettings?.resultsPanelSubheading}</div>

              <div class="macro-calculator-results-panel__protein">
                <div class="macro-calculator-results-panel__protein-target">
                  ${proteinDisplay}<span>${window.proteinCalcI18n.unitGrams}</span>
                </div>
                <div class="macro-calculator-results-panel__protein-label">${window.proteinCalcI18n.proteinLabel}</div>
              </div>

              <div class="macro-calculator-results-panel__other">
                <div class="macro-row">
                  <div>${window.proteinCalcI18n.carbsLabel}</div>
                  <div>${carbsDisplay}<span>${window.proteinCalcI18n.unitGrams}</span></div>
                </div>
                <div class="macro-row">
                  <div>${window.proteinCalcI18n.fatLabel}</div>
                  <div>${fatDisplay}<span>${window.proteinCalcI18n.unitGrams}</span></div>
                </div>
                <div class="macro-row">
                  <div>${window.proteinCalcI18n.caloriesLabel}</div>
                  <div>${caloriesDisplay}<span>${window.proteinCalcI18n.unitKcal}</span></div>
                </div>
              </div>
            </div>

            <div class="macro-calculator-results__email-link">
              <a href="#" class="btn btn__secondary" id="scrollToSignupForm">
                ${window.proteinCalcSettings?.emailLinkText || window.proteinCalcSettings.resultsEmailCta}
              </a>
            </div>
          </div>
        </section>
      `;

    // Get the main content area and update it
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.classList.add('pc-results-loading');
      // First, preserve all style tags, script tags, link tags, signup form, and recommended products
      const styleTags = Array.from(mainContent.querySelectorAll('style'));
      const scriptTags = Array.from(mainContent.querySelectorAll('script'));
      const linkTags = Array.from(mainContent.querySelectorAll('link'));
      const originalSignupForm = mainContent.querySelector('.signup-form')?.cloneNode(true);
      const originalProducts = mainContent.querySelector('#shopify-section-recommended-products')?.cloneNode(true);
      const footer = mainContent.querySelector('footer');
      const fragment = document.createDocumentFragment();
      const extraContents = document.querySelectorAll('.shopify-section-group-pc-extra-content-group');

      // Store a reference to apiData in the window object for potential use elsewhere
      window.proteinCalculatorApiData = apiData;

      // Clear main content
      Array.from(mainContent.children).forEach(child => {
        if (!child.classList.contains('cc-apps') &&
            child.tagName !== 'SCRIPT' &&
            child.tagName !== 'LINK' &&
            child.tagName !== 'FOOTER') {
          child.remove();
        }
      });

      // Add back style tags
      styleTags.forEach(style => fragment.appendChild(style));

      // Make sure script and link tags are preserved
      scriptTags.forEach(script => {
        if (!mainContent.contains(script)) {
          fragment.appendChild(script);
        }
      });

      linkTags.forEach(link => {
        if (!mainContent.contains(link)) {
          fragment.appendChild(link);
        }
      });

      // Add results panel
      const resultsSection = document.createElement('section');
      resultsSection.id = 'shopify-section-protein-calc-results';
      resultsSection.className = 'section';
      resultsSection.tabIndex = '0';
      resultsSection.innerHTML = resultsHTML;
      fragment.appendChild(resultsSection);

      // Update the results content
      const resultsPanel = document.querySelector('.macro-calculator-results-panel__content');
      if (resultsPanel) {
        resultsPanel.style.display = 'block';
      }

      // Hide loading overlay and show results
      const loadingOverlay = document.querySelector('.loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.style.display = 'none';
        // Reset body overflow to allow scrolling again
        document.body.style.overflow = 'auto';
      }

      // Show results content using global variable
      if (resultsContent) {
        resultsContent.style.display = 'block';
      }

      // Make sure the macro-calculator-results-panel and its content are visible
      const macroPanel = document.querySelector('.macro-calculator-results-panel');
      if (macroPanel) {
        macroPanel.style.display = 'block';
      }

      // Ensure the content panel is visible
      if (contentElement) {
        contentElement.style.display = 'block';
      }

      // Add macro nutrients section
      const macroNutrientsSection = document.createElement('section');
      macroNutrientsSection.id = 'shopify-section-macro-nutrients';
      macroNutrientsSection.className = 'section';

      // Create the macro nutrients content with a title
      const macroScrollAreaId = 'macro-scroll-area';
      let macroNutrientsHTML = `
        <div class="container" style="text-align: center;">
          <h2 class="macro-nutrients__heading">${window.proteinCalcSettings.macroNutrientsHeading}</h2>
          <div class="scrollable-with-controls">
            <scrollbar-carousel selector=".macro-nutrient-item" id="${macroScrollAreaId}" class="scroll-area bleed is-scrollable">
              <reveal-items selector=".macro-nutrients__list > *">
                <product-list class="macro-nutrients__list product-list">`;

      // Check if macro_nutrients exists in the response
      if (results.result.macro_nutrients && Array.isArray(results.result.macro_nutrients) && results.result.macro_nutrients.length > 0 || results.result.items && Array.isArray(results.result.items) && results.result.items.length > 0) {
        // Iterate through the macro nutrient items with full details
        const macroNutrients = results.result.macro_nutrients || results.result.items;

        // Render each macro nutrient item with the available data
        macroNutrientsHTML += macroNutrients.map(item => {
          // Get the data directly from the item
          const title = item.macro_nutrient_name || window.proteinCalcI18n.macroItemTitleFallback;
          const size = item.size || window.proteinCalcI18n.notAvailable;
          const protein = item.macro_nutrient_protein_in_grams || item.protein_in_grams || '0';
          const imageUrl = item.image || '';
          const imageHTML = imageUrl ? `<img src="${imageUrl}" alt="${title}" />` : '';

          // Use the image URL directly

          return `
            <div class="macro-nutrient-item">
              <div class="macro-nutrient-item__image">
                ${imageHTML}
              </div>
              <div class="macro-nutrient-item__content">
                <h3 class="macro-nutrient-item__title">${title}</h3>
                <div class="macro-nutrient-item__details">
                  <div class="macro-nutrient-item__size">${size}</div>
                  <div class="macro-nutrient-item__protein">${protein}${window.proteinCalcI18n.unitGrams}</div>
                </div>
              </div>
            </div>
          `;
        }).join('');
      } else {
        // If no macro nutrients are available, show a localized message
        macroNutrientsHTML += `<p class="macro-nutrients__empty">${window.proteinCalcI18n.noMacroInfo}</p>`;
      }

      // Close the carousel wrappers and add next/prev controls similar to featured-collection
      macroNutrientsHTML += `
                </product-list>
              </reveal-items>
            </scrollbar-carousel>
            <div class="floating-controls-container">
              <button is="prev-button" class="circle-button circle-button--lg circle-button--fill border group" aria-controls="${macroScrollAreaId}" disabled>
                <span class="sr-only">${window.proteinCalcI18n.a11yPrevious}</span>
              </button>
              <button is="next-button" class="circle-button circle-button--lg circle-button--fill border group" aria-controls="${macroScrollAreaId}">
                <span class="sr-only">${window.proteinCalcI18n.a11yNext}</span>
                <span class="animated-arrow"></span>
              </button>
            </div>
          </div>
        </div>
      `;

      macroNutrientsSection.innerHTML = macroNutrientsHTML;
      fragment.appendChild(macroNutrientsSection);

      // Add recommended products section
      if (originalProducts && (!recommendedProducts || recommendedProducts.length === 0)) {
        fragment.appendChild(originalProducts);
      } else {
        // Handle different formats of recommendedProducts
        let productsArray = [];

        if (Array.isArray(recommendedProducts)) {
          // If it's already an array, use it directly
          productsArray = recommendedProducts;
        } else if (typeof recommendedProducts === 'string') {
          // If it's a comma-separated string
          if (recommendedProducts.includes(',')) {
            productsArray = recommendedProducts.split(',').map(p => p.trim()).filter(p => p !== '');
          }
          // If it's a JSON string
          else if (recommendedProducts.startsWith('[') && recommendedProducts.endsWith(']')) {
            try {
              productsArray = JSON.parse(recommendedProducts);
            } catch (e) {
              console.error('Error parsing recommended products string:', e);
              productsArray = [];
            }
          }
          // If it's a single product
          else if (recommendedProducts.trim() !== '') {
            productsArray = [recommendedProducts.trim()];
          }
        }

        // First, ensure productsArray contains individual items, not a stringified array
        let processedArray = productsArray;
        if (productsArray.length === 1 && typeof productsArray[0] === 'string') {
          // Check if the single item looks like a stringified array
          const item = productsArray[0];
          if (item.startsWith('[') && item.endsWith(']')) {
            try {
              const parsed = JSON.parse(item);
              if (Array.isArray(parsed)) {
                processedArray = parsed;
              }
            } catch (e) {
              console.error('Error parsing what looks like a nested array:', e);
            }
          }
        }

        // Now extract handles from each item
        let productHandles = [];

        // Process each item in the array
        processedArray.forEach(product => {
          if (typeof product === 'string') {
            // Check if this is a comma-separated string of handles
            if (product.includes(',')) {
              // Split by comma and add each handle
              const handles = product.split(',').map(h => h.trim()).filter(h => h !== '');
              productHandles = productHandles.concat(handles);
            } else {
              // Single handle - remove any surrounding quotes or brackets
              const handle = product.trim().replace(/^["\[\s]+|["\]\s]+$/g, '');
              if (handle) productHandles.push(handle);
            }
          } else if (product && typeof product === 'object') {
            // Try to extract handle from various possible formats
            let handle = '';
            if (product.handle) handle = product.handle;
            else if (product.product_handle) handle = product.product_handle;
            else if (product.id) handle = product.id;

            if (handle) productHandles.push(handle);
          }
        });

        // Filter out any empty handles
        productHandles = productHandles.filter(handle => handle && handle.trim() !== '');

        const recommendedProductsHTML = await fetchRecommendedProducts(productHandles);
        if (recommendedProductsHTML) {
          // Create the section element
          const section = document.createElement('div');
          section.className = 'section color-scheme--transparent section-products-container-title';
          section.innerHTML = `
            <div class="container flex justify-center text-center md-down:text-center">
              <div class="max-w-text-large">
                <h2 class="section__block section__block--heading md-down:px-gutter h4">
                  <span class="contained-heading inline-block">${window.proteinCalcSettings.recommendedProductsHeading}</span>
                </h2>
              </div>
            </div>
          `;

          // Create the section wrapper
          const pcSection = document.createElement('div');
          pcSection.className = 'section color-scheme--transparent section-products recommended-products-grid container';

          // Create the products container (div)
          const productsContainer = document.createElement('div');
          productsContainer.className = 'recommended-products-container container';

          // Add custom styling for the product grid
          const gridStyle = document.createElement('style');
          gridStyle.textContent = `
            .recommended-products-container {
              width: 100%;
              max-width: 100%;
            }
            .recommended-products-row {
              display: flex;
              flex-wrap: wrap;
              margin-bottom: 20px;
            }
            .protein-calculator-product {
              padding: 0 10px;
              max-width: 250px;              
              width: 100%;
              box-sizing: border-box;
            }
            @media (max-width: 991px) {
              .protein-calculator-product {
                width: 50%;
              }
            }
            @media (min-width: 992px) and (max-width: 1199px) {
              .protein-calculator-product {
                width: 33.333%;
              }
            }
            .protein-calculator-product .card__heading {
              height: auto;
              min-height: 44px;
              width: 100%;
            }
            .protein-calculator-product .card__heading a {
              font-size: 15px;
              line-height: 1.3;
              display: inline-block;
              width: auto;
              min-width: 100%;
              white-space: nowrap;
              overflow: visible;
            }
            /* Only apply specific styling to product titles */
            .protein-calculator-product .card__heading a {
              max-width: none;
            }
            /* Make sure the product container is visible */
            .section-products {
              display: block;
              visibility: visible;
            }
            .section-products-container-title {
              display: block;
              visibility: visible;
            }
            .slider__grid {
              display: grid;
              visibility: visible;
            }
          `;
          document.head.appendChild(gridStyle);
          // Parse the HTML string into a document fragment
          const parser = new DOMParser();
          const doc = parser.parseFromString(`<div>${recommendedProductsHTML}</div>`, 'text/html');
          let productElements = Array.from(doc.querySelectorAll('.protein-calculator-product'));


          // --- SLIDER IMPLEMENTATION ---
          // Create slider wrapper
          const sliderWrapper = document.createElement('div');
          sliderWrapper.className = 'recommended-products-slider';
          sliderWrapper.style.display = 'flex';
          sliderWrapper.style.alignItems = 'center';
          sliderWrapper.style.justifyContent = 'center';
          sliderWrapper.style.width = '100%';
          sliderWrapper.style.overflow = 'hidden';

          // Add left/right arrow buttons (now in flex row, not absolute)
          const leftArrow = document.createElement('button');
          leftArrow.className = 'slider-arrow slider-arrow-left default-button';
          leftArrow.innerHTML = '&#8592;';
          leftArrow.style.position = 'static';
          leftArrow.style.height = '100%';
          leftArrow.style.width = '48px';
          leftArrow.style.flex = '0 0 48px';
          leftArrow.style.background = 'rgba(255,255,255,0.8)';
          leftArrow.style.border = 'none';
          leftArrow.style.fontSize = '2rem';
          leftArrow.style.cursor = 'pointer';
          leftArrow.style.display = 'flex';
          leftArrow.style.alignItems = 'center';
          leftArrow.style.justifyContent = 'center';

          const rightArrow = document.createElement('button');
          rightArrow.className = 'slider-arrow slider-arrow-right default-button';
          rightArrow.innerHTML = '&#8594;';
          rightArrow.style.position = 'static';
          rightArrow.style.height = '100%';
          rightArrow.style.width = '48px';
          rightArrow.style.flex = '0 0 48px';
          rightArrow.style.background = 'rgba(255,255,255,0.8)';
          rightArrow.style.border = 'none';
          rightArrow.style.fontSize = '2rem';
          rightArrow.style.cursor = 'pointer';
          rightArrow.style.display = 'flex';
          rightArrow.style.alignItems = 'center';
          rightArrow.style.justifyContent = 'center';

          // Make productsContainer a horizontal scrollable flex row
          productsContainer.style.display = 'flex';
          productsContainer.style.flexDirection = 'row';
          productsContainer.style.overflowX = 'auto';
          productsContainer.style.scrollSnapType = 'x mandatory';
          productsContainer.style.scrollBehavior = 'smooth';
          productsContainer.style.padding = '16px 0';
          productsContainer.style.margin = '0';
          productsContainer.style.webkitOverflowScrolling = 'touch';
          productsContainer.style.msOverflowStyle = 'none'; // IE/Edge
          productsContainer.style.scrollbarWidth = 'none'; // Firefox
          productsContainer.classList.add('slider-scrollable');
          productsContainer.style.display = 'flex';
          productsContainer.style.flexDirection = 'row';
          productsContainer.style.flexWrap = 'nowrap';
          productsContainer.style.scrollSnapType = 'x mandatory';
          productsContainer.style.scrollBehavior = 'smooth';
          productsContainer.style.webkitOverflowScrolling = 'touch';
          productsContainer.style.msOverflowStyle = 'none'; // IE/Edge
          productsContainer.style.scrollbarWidth = 'none'; // Firefox
          productsContainer.classList.add('slider-scrollable');
          productsContainer.style.width = '100%';
          productsContainer.style.boxSizing = 'border-box';
          productsContainer.style.overflow = 'hidden';

          // Show only 4 products at a time (handled by flex + overflow on wrapper)


          // Hide scrollbars with CSS
          const sliderStyle = document.createElement('style');
          sliderStyle.textContent = `
            .slider-scrollable::-webkit-scrollbar { display: none; }
            .slider-scrollable { -ms-overflow-style: none; scrollbar-width: none; }
            .recommended-products-slider { position: relative; width: 100%; overflow: hidden; }
            .slider-arrow { opacity: 0.7; transition: opacity 0.2s; padding: 0 25px; }
            .slider-arrow:hover { opacity: 1; }
            @media (max-width: 900px) {
              .protein-calculator-product { flex: 0 0 50% !important; max-width: 50% !important; }
            }
            @media (max-width: 600px) {
              /* .slider-arrow { display: none !important; }  // Removed to keep arrows visible on mobile */
              .protein-calculator-product { flex: 0 0 100% !important; max-width: 100% !important; }
            }
            .protein-calculator-product {
  scroll-snap-align: start;
  flex: 1 1 0;
  min-width: 0;
  max-width: none;
  box-sizing: border-box;
}
          `;
          document.head.appendChild(sliderStyle);

          // Add all products as direct children (no rows)
          productElements.forEach(product => {
            product.style.flex = '0 0 33.3333%';
            product.style.maxWidth = '33.3333%';
            product.style.scrollSnapAlign = 'start';
            productsContainer.appendChild(product);
          });

          // Slider navigation logic
          leftArrow.addEventListener('click', () => {
            productsContainer.scrollBy({ left: -productsContainer.offsetWidth * 0.8, behavior: 'smooth' });
          });
          rightArrow.addEventListener('click', () => {
            productsContainer.scrollBy({ left: productsContainer.offsetWidth * 0.8, behavior: 'smooth' });
          });

          // Assemble slider
          sliderWrapper.appendChild(leftArrow);
          sliderWrapper.appendChild(productsContainer);
          sliderWrapper.appendChild(rightArrow);

          // Append the products slider to the section
          pcSection.appendChild(sliderWrapper);

          // Append the section to mainContent
          fragment.appendChild(section);
          fragment.appendChild(pcSection);

          const productItems = document.querySelectorAll('.protein-calculator-product');

          // Make sure all product items are visible
          productItems.forEach(item => {
            item.style.display = 'block';
            item.style.visibility = 'visible';
          });

          // Process sales points for protein calculator products
          processProductSalesPoints();
        }

        const floatingButton = document.querySelector('#floatingCalculatorButton');
        if (floatingButton) {
          floatingButton.style.display = 'none';
        }
      }

      // Add and show signup form second to last
      if (originalSignupForm) {
        fragment.appendChild(originalSignupForm);
        originalSignupForm.style.display = 'block';
      } else {
        // Create new signup form HTML
        const formHTML = `
          <div class="signup-form__container container">
            <h2></h2>
            <form id="signup-form">
              <div class="form-group">
                <input type="email" id="signup_email" name="email" placeholder="${window.proteinCalcI18n.emailPlaceholder}" required>
              </div>
              <button type="submit" class="btn btn__primary"></button>
            </form>
          </div>
        `;

        // Find the target container
        const newsletterSection = document.querySelector('.newsletter.section.color-scheme--default');
        const formBlock = newsletterSection?.querySelector('.section__block.form-width');

        // Inject the form if container exists
        if (formBlock) {
          formBlock.innerHTML = formHTML;
        }
      }

      //Append the extra content last
      if (extraContents.length > 0) {
        extraContents.forEach(function (section) {
          section.style.display = 'block';
          section.style.marginBlockEnd = '16px';
          fragment.appendChild(section);
        });
      }

      // Append the fragment to mainContent
      mainContent.insertBefore(fragment, footer);
      mainContent.classList.remove('pc-results-loading');

      if (recId) {
        const scrollToSignupBtn = document.getElementById('scrollToSignupForm');
        if (scrollToSignupBtn) scrollToSignupBtn.style.display = 'none';
      }

      // Initialize Dotter embeds inside newly added product cards (non-transactional markets only)
      try { initDotterCardEmbeds(); } catch (e) { /* no-op */ }

      // Initialize the signup form
      initSignUpForm();

      // Set up scroll to signup form
      const scrollToSignupBtn = document.getElementById('scrollToSignupForm');
      if (scrollToSignupBtn) {
        scrollToSignupBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const signupForm = document.querySelector('.signup-form');
          if (signupForm) {
            // Calculate position 500px above the form
            const formRect = signupForm.getBoundingClientRect();
            const scrollPosition = window.pageYOffset + formRect.top - 500;
            // Scroll to the calculated position
            window.scrollTo({
              top: scrollPosition,
              behavior: 'smooth'
            });
          }
        });
      }

      // Create a function to check if all required content is loaded and hide the loading overlay
      const checkContentAndHideLoader = () => {
        // Check if products are loaded
        const productItems = document.querySelectorAll('.protein-calculator-product');
        const productsLoaded = productItems && productItems.length > 0;

        // Check if signup form is loaded
        const signupForm = document.querySelector('#signup-form') || document.querySelector('.signup-form');
        const signupFormLoaded = !!signupForm;

        // Only hide loading overlay when both products and signup form are loaded
        if (productsLoaded && signupFormLoaded && loadingOverlay) {
          loadingOverlay.style.display = 'none';
        } else if (loadingOverlay) {
          // If not all content is loaded yet, check again after a short delay
          requestAnimationFrame(checkContentAndHideLoader);
        }
      };

      // Start checking for content loading
      requestAnimationFrame(checkContentAndHideLoader);
    }

    // Store results in sessionStorage for email functionality
    sessionStorage.setItem('proteinCalculatorResults', JSON.stringify({
      userInfo: {
        email: apiData.email
      },
      results: {
        protein: macros.protein_grams,
        carbs: macros.carbs_grams,
        fat: macros.fat_grams,
        calories: macros.total_kcal
      }
    }));

    // Close the modal
    if (modal) {
      modal.style.display = 'none';
    }

    // Initialize the signup form
    initSignUpForm();

    // Refresh DOM references in case they've changed
    loadingOverlay = document.querySelector('.loading-overlay');
    resultsContent = document.querySelector('.results-content');
    quizContent = document.querySelector('.quiz-content');
    quizContentWrapper = document.querySelector('.quiz-content-wrapper');
    errorPanel = document.querySelector('.macro-calculator-results-panel__error');
    contentPanel = document.querySelector('.macro-calculator-results-panel__content');
    quizNavigation = document.getElementById('quizNavigation');
    progressBar = document.querySelector('.protein-calculator-modal__progress');

    // Show loading overlay and hide other elements
    if (loadingOverlay) loadingOverlay.style.display = 'flex';
    if (resultsContent) resultsContent.style.display = 'none';
    if (quizContentWrapper) quizContentWrapper.style.display = 'none';
    if (quizContent) quizContent.style.display = 'none';
    if (errorPanel) errorPanel.style.display = 'none';
    if (contentPanel) contentPanel.style.display = 'none';
    if (quizNavigation) quizNavigation.style.display = 'none';
    if (progressBar) progressBar.style.display = 'none';

    // Init yotpo widgets
    window?.yotpoWidgetsContainer?.initWidgets()
  } catch (error) {
      console.error('Error:', error);
      // Hide loading overlay and show error
      if (loadingOverlay) loadingOverlay.style.display = 'none';
      if (resultsContent) resultsContent.style.display = 'block';

      const errorEl = document.querySelector('.macro-calculator-results-panel__error');
      const contentEl = document.querySelector('.macro-calculator-results-panel__content');
      if (errorEl) errorEl.style.display = 'block';
      if (contentEl) contentEl.style.display = 'none';
    }
  }
});